/**
 * iLing Reader Page
 *
 * Core reader view with clickable tokens and a comprehensive word popover.
 *
 * 5-Status System:
 * - ignored: removed from tracking
 * - new: red, never seen
 * - review: yellow, seen but not recognized
 * - familiar: green, recognized
 * - known: default text, fully mastered
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { textsApi, vocabularyApi, languagesApi, lexiconApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getTextDirection, isRTL, isArabicScript, isChinese } from '../utils/strengthUtils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Loader2,
  Pencil,
  Trash2,
  Save,
  Globe,
  Plus,
  Minus,
  Lock,
  CheckCircle2,
  Volume2,
  Play,
  Pause,
  Square,
  AlignJustify,
  Type,
  Flag,
  Home,
  Sparkles,
  Layers,
  Headphones,
  Eye,
  ArrowDown,
  ArrowUp,
  Bookmark,
  Undo2,
  X,
  MessageCircle,
} from 'lucide-react';

// Import modular components
import { 
  WordPopoverPanel, 
  calculateSentenceRanges,
  TTS_LANG_MAP,
  AudioPlayer,
} from '../components/reader';
import { useAudioPreloader } from '../shared/reader/useAudioPreloader';
import { useWordEntry } from '../shared/reader/useWordEntry';
import { useMeasuredPagination } from '../shared/reader/useMeasuredPagination';
import { useReaderDocument } from '../shared/reader/useReaderDocument';
import { getNewPageWords, markWordsKnown } from '../shared/reader/pageWordStatus';
import { ReaderTokens } from '../components/reader/ReaderTokens';
import { ReaderControls } from '../components/reader/ReaderControls';
import { ReaderPagination } from '../components/reader/ReaderPagination';
import { WordPopoverErrorBoundary } from '../components/reader/WordPopoverErrorBoundary';
import SentenceQAPanel from '../components/reader/SentenceQAPanel';
import { LessonCompleteActions } from '../components/reader/LessonCompleteActions';
import '../components/reader/readerLayout.css';
import GrammarRecommendations from '../components/GrammarRecommendations';

// Continuous strength model
// Status labels align with backend SRS thresholds
const STATUS_CONFIG = {
  ignored:  { label: 'Ignore',   strength: null },
  new:      { label: 'New',      strength: 0.0 },
  learning: { label: 'Learning', strength: 0.2 },
  familiar: { label: 'Familiar', strength: 0.5 },
  known:    { label: 'Known',    strength: 0.8 },
  mastered: { label: 'Mastered', strength: 1.0 },
};

// Status buttons the user can click to set a word's status
const STATUS_BUTTONS = [
  { key: 'ignored',  label: 'Ignore',   color: 'var(--foreground)' },
  { key: 'new',      label: 'New',      color: '#EF4444' },        // Red
  { key: 'learning', label: 'Learning', color: '#EAB308' },        // Yellow
  { key: 'familiar', label: 'Familiar', color: '#22C55E' },        // Green
  { key: 'known',    label: 'Known',    color: 'inherit' },        // Black/White
];

const getProgressColorClass = (pct) => {
  if (pct >= 100) return 'progress-gold';
  if (pct >= 81) return 'progress-green';
  if (pct >= 30) return 'progress-yellow';
  return 'progress-red';
};

// Pre-computed confetti particles for mastered celebration (24 particles, fixed angles)
const READER_CONFETTI_PARTICLES = (() => {
  const colors = ['#FFD700','#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F0E68C','#87CEEB','#FFB347','#77DD77'];
  const dists  = [80, 100, 70, 110, 85, 95, 75, 105, 90, 115, 65, 100, 80, 90, 70, 105, 95, 85, 75, 110, 90, 80, 100, 70];
  return Array.from({ length: 24 }, (_, i) => {
    const angle = (i * 15) * (Math.PI / 180);
    return {
      tx:      Math.cos(angle) * dists[i],
      ty:      Math.sin(angle) * dists[i] - 25, // slight upward bias
      color:   colors[i % colors.length],
      size:    5 + (i % 4) * 2,
      height:  i % 3 === 1 ? 3 + (i % 3) : 5 + (i % 4) * 2, // mix rects and circles
      isCircle: i % 3 !== 1,
      delay:   (i % 6) * 0.045,
    };
  });
})();

export default function Reader() {
  const { textId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readerRef = useRef(null);
  const closePopoverRef = useRef(null);
  const lastClickedTokenIdxRef = useRef(null);   // global allTokens index of last clicked term
  const lastExprClickedIdxRef = useRef(null);    // token idx that last opened an expression popover
  const pendingSentenceJumpRef = useRef(false);   // true when sentence mode was just activated
  // Token index to jump to when sentence mode activates (null = no jump pending, -1 = first sentence)
  const sentenceJumpTokenRef = useRef(null);

  // Core state
  const { readerData, setReaderData, loading, loadingMore, fetchReaderData, loadMore, textError } = useReaderDocument(textId, navigate);
  const prefetchedOffset = useRef(null);
  const [showStats, setShowStats] = useState(true);
  const [grammarCollapsed, setGrammarCollapsed] = useState(true);
  const [grammarDismissed, setGrammarDismissed] = useState(false);

  // Selected word / popover
  const [selectedToken, setSelectedToken] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  // Drag state for the Oracle panel
  const isDraggingRef = useRef(false);
  const dragOriginRef = useRef(null); // { mx, my, px, py }

  // Audio preloader (#27-29)
  const langCode = readerData?.language?.code;
  const { wordData, setWordData, wordLoading, loadEntry } = useWordEntry({ languageCode: langCode });

  // Edit mode
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Save to language
  const [showSaveLangDialog, setShowSaveLangDialog] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [markAsKnown, setMarkAsKnown] = useState(false);

  // Flashcard list modal
  const [showFlashcardModal, setShowFlashcardModal] = useState(false);
  const [flashcardLists, setFlashcardLists] = useState([]);
  const [selectedFlashcardList, setSelectedFlashcardList] = useState('');
  const [newFlashcardListName, setNewFlashcardListName] = useState('');
  const [savingFlashcard, setSavingFlashcard] = useState(false);
  // Mastered celebration confetti
  const [masteredTokenInfo, setMasteredTokenInfo] = useState(null);
  const masteredWordsShownRef = useRef(new Set());

  // Popover form
  const [noteText, setNoteText] = useState('');
  const [translationText, setTranslationText] = useState('');
  const [translationSaved, setTranslationSaved] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Expression/Span selection (for multi-word)
  const [selectedSpan, setSelectedSpan] = useState(null); // { startIdx, endIdx, tokens: [], surfaceForm: '' }
  const [expressionTranslation, setExpressionTranslation] = useState(null);
  const audioPreloader = useAudioPreloader(langCode, !!selectedToken || !!selectedSpan);
  const isExpressionMode = selectedSpan && selectedSpan.tokens?.length > 1;

  // Undo stack for status changes
  const [undoStack, setUndoStack] = useState([]); // [{word, langCode, prevStatus, newStatus, timestamp}]
  
  // Detected expressions in the text (from saved lexicon)
  const [detectedExpressions, setDetectedExpressions] = useState([]); // [{ entry, user_state, start_idx, end_idx }, ...]
  const [hoveredExpressionIdx, setHoveredExpressionIdx] = useState(null);
  
  // Expression popover state
  const [selectedExpressionEntry, setSelectedExpressionEntry] = useState(null);
  const [expressionInFullOracle, setExpressionInFullOracle] = useState(false);
  const [expressionLoading, setExpressionLoading] = useState(false);
  const [expressionSaving, setExpressionSaving] = useState(false);

  // Detected verb formations in the text
  const [detectedVerbFormations, setDetectedVerbFormations] = useState([]);
  const [hoveredVerbFormationIdx, setHoveredVerbFormationIdx] = useState(null);
  const [selectedVerbFormationEntry, setSelectedVerbFormationEntry] = useState(null);
  const [verbFormationInFullOracle, setVerbFormationInFullOracle] = useState(false);
  const [verbFormationSaving, setVerbFormationSaving] = useState(false);

  // Reading preferences
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('iling-reader-font-size');
    return saved ? parseInt(saved, 10) : 18;
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [showPagePrompt, setShowPagePrompt] = useState(false);
  const [pendingPageDirection, setPendingPageDirection] = useState(null); // 'next' or 'prev'
  const [markingPage, setMarkingPage] = useState(false);
  const [pageMarkError, setPageMarkError] = useState('');

  // Reader width resize (#17 — grabbable edge)
  const [readerWidth, setReaderWidth] = useState(() => {
    const saved = localStorage.getItem('iling-reader-width');
    return saved ? parseInt(saved, 10) : 0; // 0 = auto (max-w-4xl)
  });
  const isResizing = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  
  // Sentence Mode - view one sentence at a time with clickable tokens
  const [sentenceMode, setSentenceMode] = useState(false);
  
  // TTS Full Text Playback
  const [isPlayingFullText, setIsPlayingFullText] = useState(false);
  const [isPlayingSentence, setIsPlayingSentence] = useState(false);
  const [showReaderSpeedPicker, setShowReaderSpeedPicker] = useState(false);
  const [readerPlaybackRate, setReaderPlaybackRate] = useState(1.0);

  // Sentence translation
  const [sentenceTranslation, setSentenceTranslation] = useState(null);
  const [sentenceTranslating, setSentenceTranslating] = useState(false);

  // Sentence QA panel
  const [showQAPanel, setShowQAPanel] = useState(false);
  const [sentenceContext, setSentenceContext] = useState('');
  const ttsRef = useRef(null);
  const textContainerRef = useRef(null);
  const measurementTemplateRef = useRef(null);

  // Reader edge resize handlers (#17)
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    resizeStartX.current = e.clientX;
    const container = document.querySelector('.reader-container');
    resizeStartWidth.current = container?.offsetWidth || 896;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      if (!isResizing.current) return;
      const delta = ev.clientX - resizeStartX.current;
      const newWidth = Math.max(400, Math.min(window.innerWidth - 32, resizeStartWidth.current + delta * 2));
      setReaderWidth(newWidth);
    };
    const onUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Persist
      const container = document.querySelector('.reader-container');
      if (container) localStorage.setItem('iling-reader-width', String(container.offsetWidth));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);


  // Lesson completion tracking
  const [completionData, setCompletionData] = useState(null);

  // Background enrichment bar
  const [enrichJob, setEnrichJob] = useState(null);   // { job_id, total, priority_count }
  const [enrichProgress, setEnrichProgress] = useState(null); // polling data
  const enrichPollRef = useRef(null);

  // ── Poll enrichment job status ──
  useEffect(() => {
    if (!enrichJob?.job_id) return;

    const poll = async () => {
      try {
        const res = await vocabularyApi.getReaderEnrichStatus(enrichJob.job_id);
        const p = res.data;
        setEnrichProgress(p);

        if (p.complete || p.not_found) {
          clearInterval(enrichPollRef.current);
          enrichPollRef.current = null;
          fetchCompletionData(); // final refresh of canonization bar
          return;
        }

        // Once priority is done: keep bar visible for phase 2, slow poll
        if (p.priority_done && !p.complete) {
          fetchCompletionData();
          clearInterval(enrichPollRef.current);
          enrichPollRef.current = setInterval(poll, 5000);
        }
      } catch { /* silent */ }
    };

    enrichPollRef.current = setInterval(poll, 1200); // fast until priority done
    poll(); // immediate first check
    return () => { clearInterval(enrichPollRef.current); enrichPollRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichJob?.job_id]);

  // Finish Text modal
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finishStats, setFinishStats] = useState(null);
  const [finishLoading, setFinishLoading] = useState(false);
  const [finishActionBusy, setFinishActionBusy] = useState(false);
  
  // Multi-word selection for expressions
  const [selectedExpression, setSelectedExpression] = useState(null);

  // Pagination — must be before early returns (Rules of Hooks)
  const allTokens = useMemo(() => readerData?.tokens ?? [], [readerData?.tokens]);
  const sentenceRanges = useMemo(() => calculateSentenceRanges(allTokens), [allTokens]);

  // Build a map of token index -> expression info for efficient lookup
  const expressionMap = useMemo(() => {
    const map = new Map(); // tokenIndex -> { expression, isStart, isEnd, isMiddle }
    
    detectedExpressions.forEach((expr, exprIdx) => {
      for (let i = expr.start_idx; i <= expr.end_idx; i++) {
        map.set(i, {
          expression: expr,
          expressionIdx: exprIdx,
          isStart: i === expr.start_idx,
          isEnd: i === expr.end_idx,
          isMiddle: i > expr.start_idx && i < expr.end_idx
        });
      }
    });
    
    return map;
  }, [detectedExpressions]);

  // Callback passed into useMeasuredPagination so every measure() call (including
  // ResizeObserver-triggered ones on mobile) uses the pending jump target rather than
  // the anchor.  Returns null when no jump is pending.
  const getSentenceJumpPage = useCallback((pages) => {
    if (sentenceJumpTokenRef.current === null) return null;
    const tokenIdx = sentenceJumpTokenRef.current;
    if (tokenIdx === -1) return 0; // no prior click → first sentence
    const sentIdx = sentenceRanges.findIndex(r => tokenIdx >= r.start && tokenIdx < r.end);
    if (sentIdx < 0) return null;
    const pageIndex = pages.findIndex(p => p.sentenceIndex === sentIdx);
    return pageIndex >= 0 ? pageIndex : null;
  }, [sentenceRanges]);

  const pagination = useMeasuredPagination({ tokens: allTokens, sentenceRanges, sentenceMode, viewportRef: textContainerRef, templateRef: measurementTemplateRef, fontSize, languageCode: langCode, expressionMap, currentPage, setCurrentPage, textId, getSentenceJumpPage });
  const totalPages = pagination.pages.length;
  const activePage = pagination.pages[currentPage];
  const pageTokens = useMemo(() => (activePage?.tokens || []).map(fragment => ({ ...allTokens[fragment._readerIndex], _readerIndex: fragment._readerIndex, _readerText: fragment._readerText, _readerOffset: fragment._readerOffset })), [activePage, allTokens]);
  const hasMoreTokens = !!readerData?.pagination?.has_more;
  useEffect(() => {
    const key = `${textId}:${readerData?.pagination?.next_offset}`;
    if (!loading && pagination.ready && totalPages > 0 && currentPage >= totalPages - 3 && hasMoreTokens && !loadingMore && prefetchedOffset.current !== key) {
      prefetchedOffset.current = key;
      loadMore();
    }
  }, [loading, pagination.ready, totalPages, currentPage, textId, readerData?.pagination?.next_offset, hasMoreTokens, loadingMore, loadMore]);
  const currentSentenceIndex = sentenceMode ? (activePage?.sentenceIndex || 0) : 0;
  const newPageWords = useMemo(() => getNewPageWords(pageTokens), [pageTokens]);
  const newWordsOnPage = newPageWords.length;

  // Helper to get expression info for a token
  const getExpressionForToken = useCallback((globalTokenIdx) => {
    return expressionMap.get(globalTokenIdx);
  }, [expressionMap]);

  // Build a map from global token index → verb formation info (for click detection)
  const verbFormationMap = useMemo(() => {
    const map = new Map();
    if (!allTokens.length || !detectedVerbFormations.length) return map;
    const wordTokens = allTokens
      .map((t, i) => ({ token: t, globalIdx: i }))
      .filter(({ token }) => token.is_word);
    detectedVerbFormations.forEach((vf, vfIdx) => {
      const components = (vf.entry?.components || []).map(c => c.toLowerCase().trim());
      if (!components.length) return;
      for (let wi = 0; wi <= wordTokens.length - components.length; wi++) {
        const match = components.every((comp, ci) => {
          const t = wordTokens[wi + ci]?.token;
          return t?.normalized_form?.toLowerCase() === comp || t?.surface_form?.toLowerCase() === comp;
        });
        if (match) {
          const startGlobal = wordTokens[wi].globalIdx;
          const endGlobal = wordTokens[wi + components.length - 1].globalIdx;
          for (let i = startGlobal; i <= endGlobal; i++) {
            map.set(i, { formation: vf, formationIdx: vfIdx, isStart: i === startGlobal, isEnd: i === endGlobal });
          }
          break;
        }
      }
    });
    return map;
  }, [detectedVerbFormations, allTokens]);

  // Handle page navigation with prompt for new words
  const handlePageChange = useCallback((direction) => {
    if (direction === 'next' && currentPage >= totalPages - 1 && hasMoreTokens) {
      loadMore();
      return;
    }
    if (direction === 'next' && newWordsOnPage > 0) {
      // Show prompt if there are new words
      setPendingPageDirection('next');
      setPageMarkError('');
      setShowPagePrompt(true);
    } else {
      // Navigate directly
      if (direction === 'next') {
        setCurrentPage(p => Math.min(p + 1, totalPages - 1));
      } else {
        setCurrentPage(p => Math.max(p - 1, 0));
      }
    }
  }, [newWordsOnPage, totalPages, currentPage, hasMoreTokens, loadMore]);

  // Detect saved expressions in the text tokens
  const detectExpressionsInText = useCallback(async () => {
    if (!readerData?.language?.code || !pagination.geometryTokens?.length) return;
    
    try {
      // Get normalized forms of all word tokens
      const normalizedTokens = pagination.geometryTokens.map(t => t.normalized_form || t.surface_form?.toLowerCase() || '');
      
      const { data } = await lexiconApi.detectExpressions(
        readerData.language.code,
        normalizedTokens
      );
      
      if (data.expressions?.length > 0) {
        setDetectedExpressions(data.expressions);
        console.log(`Detected ${data.expressions.length} expressions in text`);
      } else {
        setDetectedExpressions([]);
      }
    } catch (err) {
      console.error('Failed to detect expressions:', err);
      setDetectedExpressions([]);
    }
  }, [readerData?.language?.code, pagination.geometryTokens]);

  // Detect expressions when reader data loads
  useEffect(() => {
    detectExpressionsInText();
  }, [detectExpressionsInText]);

  // Detect verb formations in the text
  const detectVerbFormationsInText = useCallback(async () => {
    if (!readerData?.language?.code || !pagination.geometryTokens?.length) return;
    try {
      const tokens = pagination.geometryTokens
        .filter(t => t.is_word)
        .map(t => ({ normalized_form: t.normalized_form || t.surface_form?.toLowerCase() || '', surface_form: t.surface_form || '' }));
      const { data } = await lexiconApi.detectVerbFormations(readerData.language.code, tokens);
      setDetectedVerbFormations(data.formations || []);
    } catch {
      setDetectedVerbFormations([]);
    }
  }, [readerData?.language?.code, pagination.geometryTokens]);

  useEffect(() => { detectVerbFormationsInText(); }, [detectVerbFormationsInText]);

  // Mark all new words on page as known
  const markPageNewWordsAsKnown = useCallback(async () => {
    if (markingPage) return false;
    if (newPageWords.length === 0) return true;
    setMarkingPage(true);
    setPageMarkError('');
    try {
      const { data } = await vocabularyApi.batchSetWordStatus(newPageWords, readerData.language.code, 'known');
      if (data.updated !== newPageWords.length) throw new Error('Not all words were saved');
      setReaderData(previous => ({ ...previous, tokens: markWordsKnown(previous.tokens, newPageWords) }));
      toast.success(`Marked ${data.updated} words as known`);
      return true;
    } catch (err) {
      console.error('Failed to mark words as known:', err);
      setPageMarkError('Could not save all words. Please retry or skip without marking.');
      return false;
    } finally {
      setMarkingPage(false);
    }
  }, [newPageWords, readerData?.language?.code, markingPage]);

  // Fetch lesson completion status
  const fetchCompletionData = useCallback(async () => {
    try {
      const res = await textsApi.getCompletion(textId);
      setCompletionData(res.data);
    } catch (err) {
      console.error('Failed to fetch completion data:', err);
    }
  }, [textId]);

  useEffect(() => {
    fetchReaderData(); fetchCompletionData(); setCurrentPage(0);
    // Reset enrichment state when text changes
    setEnrichJob(null);
    setEnrichProgress(null);
    setShowStats(true);
  }, [fetchReaderData, fetchCompletionData]);

  // ── Auto-enrich: start background enrichment when text loads ──
  useEffect(() => {
    if (!readerData?.text?.text_id || !readerData?.language?.code || !allTokens.length) return;

    const textId = readerData.text.text_id;
    const langCode = readerData.language.code;

    // Snapshot the current page's words now (for priority ordering)
    const pageWordSet = new Set(
      (pageTokens || [])
        .filter(t => t.is_word && t.normalized_form)
        .map(t => t.normalized_form.toLowerCase())
    );

    // Fetch ALL tokens for the text (up to 10 000) so the total always
    // represents the whole text, not just the current pagination chunk.
    textsApi.getTokens(textId)
      .then(({ data: allTextTokens }) => {
        const tokens = Array.isArray(allTextTokens) ? allTextTokens : [];
        const seen = new Set();
        const priorityWords = [];   // current-page words first
        const remainingWords = [];  // rest of the text

        for (const t of tokens) {
          if (!t.is_word || !t.normalized_form) continue;
          const key = t.normalized_form.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          if (pageWordSet.has(key)) {
            priorityWords.push(t.normalized_form);
          } else {
            remainingWords.push(t.normalized_form);
          }
        }

        const ordered = [...priorityWords, ...remainingWords];
        if (ordered.length === 0) return;

        const pc = Math.min(150, Math.max(50, priorityWords.length));

        vocabularyApi.startReaderEnrich(ordered, langCode, pc)
          .then(res => {
            setEnrichJob(res.data);
            setEnrichProgress({
              done: 0, enriched: 0, total: res.data.total,
              priority_done: false, priority_count: res.data.priority_count, complete: false
            });
          })
          .catch(err => console.warn('[reader-enrich] failed to start:', err));
      })
      .catch(err => console.warn('[reader-enrich] failed to fetch tokens:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readerData?.text?.text_id, readerData?.language?.code, allTokens.length > 0]);

  // Only fetch existing audio. Page navigation must not start AI or TTS jobs.
  useEffect(() => {
    if (!pageTokens?.length || !langCode) return;
    const uniqueWords = [...new Set(
      pageTokens
        .filter(t => t.is_word && t.normalized_form)
        .map(t => t.normalized_form)
    )];
    audioPreloader.preloadWords(uniqueWords);
    return audioPreloader.cancelPreload;
  }, [pageTokens, langCode, audioPreloader.preloadWords, audioPreloader.cancelPreload]);

  // Passive exposure: refresh known/familiar words when they appear on the page.
  // Only fires for words at 'known' or 'familiar' status — the backend will skip
  // any that the user manually placed at those tiers (they need clicks to advance).
  useEffect(() => {
    if (!pageTokens?.length || !langCode || !textId) return;
    const wordsToTrack = [
      ...new Set(
        pageTokens
          .filter(t => t.is_word && t.normalized_form && (t.status === 'known' || t.status === 'familiar'))
          .map(t => t.normalized_form)
      )
    ];
    if (!wordsToTrack.length) return;
    vocabularyApi.trackExposures(
      wordsToTrack.map(word => ({
        word,
        language_code: langCode,
        event_type: 'passive',
        text_id: textId,
      }))
    ).catch(() => {});
  }, [pageTokens, langCode, textId]);


  useEffect(() => {
    languagesApi.getAll()
      .then((res) => setAvailableLanguages(res.data || []))
      .catch((err) => console.warn('Failed to fetch languages:', err?.message || err));
  }, []);

  // Keep ref pointed at the latest closePopover so the event listener is never stale
  useEffect(() => { closePopoverRef.current = closePopover; });

  // Close popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-popover-panel]') && !e.target.closest('.reader-token')) {
        closePopoverRef.current?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []); // Empty — safe because we read via ref

  // Keyboard shortcuts are handled by WordPopover when the Oracle is open.
  // A separate Reader-level handler is not needed: when selectedToken is null
  // the Oracle is closed (no-op); when selectedToken is non-null the Oracle is
  // open and WordPopover's own keydown listener already handles X/R/F/K/Escape.
  // Having both listeners causes a double API call on every hotkey press.

  // ---------- Expression click ----------
  const handleExpressionClick = async (expression, e) => {
    // Capture geometry immediately — currentTarget is nulled by React after event dispatch
    const el = e?.currentTarget || e?.target;
    const rect = el?.getBoundingClientRect?.();
    if (!rect) return;

    // Close any open word popover
    setSelectedToken(null);
    setWordData(null);
    setSelectedSpan(null);
    // Track last clicked position (start of expression)
    if (expression.start_idx != null) lastClickedTokenIdxRef.current = expression.start_idx;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 768;
    const popoverWidth = isMobile ? Math.min(340, viewportWidth - 20) : 400;
    const popoverMaxHeight = Math.min(viewportHeight - 40, isMobile ? 420 : 580);
    
    const marginRight = 15;
    const marginLeft = isMobile ? 10 : 230;
    const marginTop = 10;
    const marginBottom = 10;
    
    let top, left;
    const spaceOnRight = viewportWidth - rect.right - marginRight;
    const spaceOnLeft = rect.left - marginLeft;
    const minLeft = marginLeft;
    const maxLeft = viewportWidth - popoverWidth - marginRight;
    
    if (spaceOnRight >= popoverWidth + 10) {
      left = rect.right + 10;
    } else if (spaceOnLeft >= popoverWidth + 10) {
      left = rect.left - popoverWidth - 10;
    } else {
      left = Math.max(minLeft, (viewportWidth - popoverWidth) / 2);
    }
    left = Math.max(minLeft, Math.min(left, maxLeft));
    
    const spaceBelow = viewportHeight - rect.bottom - marginBottom;
    const spaceAbove = rect.top - marginTop;
    
    if (spaceBelow >= popoverMaxHeight + 10) {
      top = rect.bottom + 8;
    } else if (spaceAbove >= popoverMaxHeight + 10) {
      top = rect.top - popoverMaxHeight - 8;
    } else {
      if (spaceBelow >= spaceAbove) {
        top = rect.bottom + 8;
      } else {
        top = Math.max(marginTop, rect.top - popoverMaxHeight - 8);
      }
    }
    
    const minTop = marginTop;
    const maxTop = viewportHeight - popoverMaxHeight - marginBottom;
    top = Math.max(minTop, Math.min(top, maxTop));
    
    setPopoverPos({ top, left, isMobile, maxHeight: popoverMaxHeight });
    setSelectedExpressionEntry(expression);
    setExpressionInFullOracle(false);
    setExpressionLoading(false);
  };

  // Close expression popover
  const closeExpressionPopover = () => {
    lastExprClickedIdxRef.current = null;
    setSelectedExpressionEntry(null);
    setPopoverPos(null);
    setExpressionInFullOracle(false);
  };

  // Close VF mini card
  const closeVerbFormationMiniCard = () => {
    setSelectedVerbFormationEntry(null);
    setVerbFormationInFullOracle(false);
    if (!selectedToken && !selectedExpressionEntry) setPopoverPos(null);
  };

  // Open VF oracle from a token click — positions popover then opens full oracle
  const handleVerbFormationClick = (vf, e) => {
    const el = e?.currentTarget || e?.target;
    const rect = el?.getBoundingClientRect?.();
    if (!rect) return;
    setSelectedToken(null);
    setWordData(null);
    setSelectedSpan(null);
    setExpressionInFullOracle(false);
    setSelectedExpressionEntry(null);
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 768;
    const popoverWidth = isMobile ? Math.min(340, viewportWidth - 20) : 400;
    const popoverMaxHeight = Math.min(viewportHeight - 40, isMobile ? 420 : 580);
    const marginRight = 15;
    const marginLeft = isMobile ? 10 : 230;
    const marginTop = 10;
    const marginBottom = 10;
    let top, left;
    const spaceOnRight = viewportWidth - rect.right - marginRight;
    const spaceOnLeft = rect.left - marginLeft;
    const minLeft = marginLeft;
    const maxLeft = viewportWidth - popoverWidth - marginRight;
    if (spaceOnRight >= popoverWidth + 10) {
      left = rect.right + 10;
    } else if (spaceOnLeft >= popoverWidth + 10) {
      left = rect.left - popoverWidth - 10;
    } else {
      left = Math.max(minLeft, (viewportWidth - popoverWidth) / 2);
    }
    left = Math.max(minLeft, Math.min(left, maxLeft));
    const spaceBelow = viewportHeight - rect.bottom - marginBottom;
    const spaceAbove = rect.top - marginTop;
    if (spaceBelow >= popoverMaxHeight + 10) {
      top = rect.bottom + 8;
    } else if (spaceAbove >= popoverMaxHeight + 10) {
      top = rect.top - popoverMaxHeight - 8;
    } else {
      top = spaceBelow >= spaceAbove
        ? rect.bottom + 8
        : Math.max(marginTop, rect.top - popoverMaxHeight - 8);
    }
    const minTop = marginTop;
    const maxTop = viewportHeight - popoverMaxHeight - marginBottom;
    top = Math.max(minTop, Math.min(top, maxTop));
    setPopoverPos({ top, left, isMobile, maxHeight: popoverMaxHeight });
    setSelectedVerbFormationEntry({ entry: vf.entry, user_state: vf.user_state || { status: 'new' } });
    setVerbFormationInFullOracle(false);  // start as mini card, user expands with tab
  };

  // Set expression status
  const handleSetExpressionStatus = async (status) => {
    if (!selectedExpressionEntry) return;
    
    setActionLoading(true);
    try {
      await lexiconApi.setExpressionUserState(selectedExpressionEntry.entry.entry_id, { status });
      toast.success(`Expression marked as ${status}`);
      
      // Update local state immediately for instant UI feedback
      setSelectedExpressionEntry(prev => ({
        ...prev,
        user_state: { ...(prev.user_state || {}), status }
      }));
      
      // Also update detectedExpressions state so token colors update
      setDetectedExpressions(prevExprs => 
        prevExprs.map(expr => 
          expr.entry.entry_id === selectedExpressionEntry.entry.entry_id
            ? { ...expr, user_state: { ...(expr.user_state || {}), status } }
            : expr
        )
      );
    } catch (err) {
      console.error('Failed to update expression status:', err);
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // AI enrich expression
  const handleEnrichExpression = async () => {
    if (!selectedExpressionEntry) return;
    
    setExpressionLoading(true);
    try {
      const { data } = await lexiconApi.enrichExpression(selectedExpressionEntry.entry.entry_id);
      if (data.success) {
        // Update the expression entry with enriched data
        setSelectedExpressionEntry(prev => ({
          ...prev,
          entry: data.entry
        }));
        toast.success('Expression enriched by AI');
        
        // Also refresh the detected expressions
        detectExpressionsInText();
      }
    } catch (err) {
      console.error('Failed to enrich expression:', err);
      toast.error('Failed to enrich expression');
    } finally {
      setExpressionLoading(false);
    }
  };

  // ---------- Token click ----------
  const handleTokenClick = async (token, e, tokenIndex = null) => {
    if (!token.is_word) return;
    audioPreloader.cancelPreload();

    // Find token index in allTokens (not pageTokens) for consistency with expand/shrink handlers
    let idx = tokenIndex;
    if (idx === null) {
      idx = allTokens.findIndex(t => t.token_id === token.token_id);
    }

    // Check if this token is part of a detected expression
    const exprInfo = getExpressionForToken(idx);
    
    if (exprInfo) {
      // Second click on same open expression → break through to word Oracle
      const isSameExpr = selectedExpressionEntry?.entry?.entry_id === exprInfo.expression.entry.entry_id;
      if (isSameExpr) {
        lastExprClickedIdxRef.current = null;
        setSelectedExpressionEntry(null);
        // fall through to word-Oracle logic below
      } else {
        // First click (or different expression) — open expression popover
        lastExprClickedIdxRef.current = idx;
        handleExpressionClick(exprInfo.expression, e);
        return;
      }
    }

    // Fallback: scan detectedExpressions by token_id in case of index mismatch
    const exprByToken = !exprInfo && detectedExpressions.find(expr => {
      for (let i = expr.start_idx; i <= expr.end_idx; i++) {
        if (allTokens[i]?.token_id === token.token_id) return true;
      }
      return false;
    });
    if (exprByToken) {
      const isSameExpr = selectedExpressionEntry?.entry?.entry_id === exprByToken.entry.entry_id;
      if (isSameExpr) {
        lastExprClickedIdxRef.current = null;
        setSelectedExpressionEntry(null);
        // fall through to word-Oracle logic below
      } else {
        lastExprClickedIdxRef.current = idx;
        handleExpressionClick(exprByToken, e);
        return;
      }
    }

    // Check if this token is part of a detected verb formation
    const vfInfo = verbFormationMap.get(idx);
    if (vfInfo) {
      const isSameVF = selectedVerbFormationEntry?.entry?.entry_id === vfInfo.formation.entry?.entry_id;
      if (isSameVF && verbFormationInFullOracle) {
        // Second click on same open VF oracle → clear VF state, fall through to word oracle
        setVerbFormationInFullOracle(false);
        setSelectedVerbFormationEntry(null);
        // fall through to word-Oracle logic below
      } else {
        // First click (or different VF) — open VF oracle
        handleVerbFormationClick(vfInfo.formation, e);
        return;
      }
    }

    // Opening a word popover — close any open expression/VF oracle
    setSelectedExpressionEntry(null);
    setVerbFormationInFullOracle(false);
    setSelectedVerbFormationEntry(null);
    lastClickedTokenIdxRef.current = idx;

    // Position the popover using fixed positioning (viewport-relative)
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 768;
    const popoverWidth = isMobile ? Math.min(320, viewportWidth - 20) : 380;
    const popoverMaxHeight = Math.min(viewportHeight - 40, isMobile ? 400 : 550);
    
    // Safe margins - account for sidebar and edge padding
    const marginRight = 15;
    const marginLeft = isMobile ? 10 : 230; // Account for sidebar on desktop
    const marginTop = 10;
    const marginBottom = 10;
    
    // Calculate optimal position
    let top, left;
    
    // Horizontal positioning - calculate available space
    const spaceOnRight = viewportWidth - rect.right - marginRight;
    const spaceOnLeft = rect.left - marginLeft;
    
    // Calculate bounds
    const minLeft = marginLeft;
    const maxLeft = viewportWidth - popoverWidth - marginRight;
    
    if (spaceOnRight >= popoverWidth + 10) {
      // Position to the right of the word
      left = rect.right + 10;
    } else if (spaceOnLeft >= popoverWidth + 10) {
      // Position to the left of the word
      left = rect.left - popoverWidth - 10;
    } else {
      // Center horizontally if no space on sides
      left = Math.max(minLeft, (viewportWidth - popoverWidth) / 2);
    }
    
    // Clamp horizontal position to viewport bounds
    left = Math.max(minLeft, Math.min(left, maxLeft));
    
    // Vertical positioning - prefer below, then above, then fit in view
    const spaceBelow = viewportHeight - rect.bottom - marginBottom;
    const spaceAbove = rect.top - marginTop;
    
    if (spaceBelow >= popoverMaxHeight + 10) {
      top = rect.bottom + 8;
    } else if (spaceAbove >= popoverMaxHeight + 10) {
      top = rect.top - popoverMaxHeight - 8;
    } else {
      // Position where there's more space and let scrollbar handle overflow
      if (spaceBelow >= spaceAbove) {
        top = rect.bottom + 8;
      } else {
        top = Math.max(marginTop, rect.top - popoverMaxHeight - 8);
      }
    }
    
    // Final vertical bounds check - keep fully in viewport
    const minTop = marginTop;
    const maxTop = viewportHeight - popoverMaxHeight - marginBottom;
    top = Math.max(minTop, Math.min(top, maxTop));
    
    setPopoverPos({ top, left, isMobile, maxHeight: popoverMaxHeight });

    // Extract sentence context for the "In Context" feature
    (() => {
      let ctx = '';
      if (sentenceMode && currentSentenceTokens.length > 0) {
        ctx = currentSentenceTokens.map(t => t.surface_form || '').join('').trim();
      } else {
        const pageIdx = pageTokens.findIndex(t => t.token_id === token.token_id);
        if (pageIdx >= 0) {
          ctx = pageTokens.slice(Math.max(0, pageIdx - 20), pageIdx + 21)
            .map(t => t.surface_form || '').join('').trim();
        }
      }
      setSentenceContext(ctx);
    })();

    setSelectedToken(token);
    
    // Initialize span with single token
    setSelectedSpan({
      startIdx: idx,
      endIdx: idx,
      tokens: [token],
      surfaceForm: token.surface_form?.trim() || ''
    });
    
    setTranslationSaved(false);
    setNoteText('');
    setTranslationText('');
    const data = await loadEntry(token.normalized_form);
    if (data) {
      setNoteText(data.user_state?.notes || '');
      setTranslationText(data.user_state?.translation || '');
    }
  };

  const closePopover = async () => {
    // Finalize: persist word data to DB so lock survives page refresh
    if (selectedToken && wordData?.definitions?.length && !wordData.locked && !wordData._details_loading) {
      const langCode = readerData?.language?.code || 'en';
      vocabularyApi.finalizeWordData(selectedToken.normalized_form, langCode)
        .then(() => fetchCompletionData())
        .catch(err => console.warn('Failed to finalize word data:', err?.message || err));
    }
    setSelectedToken(null);
    setWordData(null);
    setPopoverPos(null);
    setSelectedSpan(null);
    setExpressionInFullOracle(false);
    setSelectedExpressionEntry(null);
  };

  // ---------- Oracle drag-to-reposition ----------
  const handleOracleDragStart = (e) => {
    // Support both mouse and touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    if (!popoverPos) return;
    isDraggingRef.current = true;
    dragOriginRef.current = { mx: clientX, my: clientY, px: popoverPos.left, py: popoverPos.top };
    e.preventDefault();

    const onMove = (ev) => {
      if (!isDraggingRef.current) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - dragOriginRef.current.mx;
      const dy = cy - dragOriginRef.current.my;
      setPopoverPos(prev => prev ? { ...prev, left: dragOriginRef.current.px + dx, top: dragOriginRef.current.py + dy } : prev);
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  };

  // ---------- Expand Selection (← →) ----------
  const handleExpandLeft = () => {
    if (!selectedSpan) return;
    const { startIdx, endIdx, tokens: spanTokens } = selectedSpan;
    
    // Find the previous word token
    let newStartIdx = startIdx - 1;
    while (newStartIdx >= 0 && !allTokens[newStartIdx]?.is_word) {
      newStartIdx--;
    }
    
    if (newStartIdx < 0) {
      toast.info('No more words to the left');
      return;
    }
    
    // Build new span
    const newTokens = [];
    for (let i = newStartIdx; i <= endIdx; i++) {
      if (allTokens[i]) newTokens.push(allTokens[i]);
    }
    
    // Build surface form including punctuation/spaces
    const surfaceForm = newTokens.map(t => t.surface_form).join('').trim();
    
    setSelectedSpan({
      startIdx: newStartIdx,
      endIdx,
      tokens: newTokens.filter(t => t.is_word),
      surfaceForm
    });
    
    // Note: We keep wordData cached so shrinking back to single word doesn't require re-fetch
    // The popover UI conditionally hides word-specific sections when isExpressionMode is true
  };

  const handleExpandRight = () => {
    if (!selectedSpan) return;
    const { startIdx, endIdx, tokens: spanTokens } = selectedSpan;
    
    // Find the next word token
    let newEndIdx = endIdx + 1;
    while (newEndIdx < allTokens.length && !allTokens[newEndIdx]?.is_word) {
      newEndIdx++;
    }
    
    if (newEndIdx >= allTokens.length) {
      toast.info('No more words to the right');
      return;
    }
    
    // Build new span
    const newTokens = [];
    for (let i = startIdx; i <= newEndIdx; i++) {
      if (allTokens[i]) newTokens.push(allTokens[i]);
    }
    
    // Build surface form including punctuation/spaces
    const surfaceForm = newTokens.map(t => t.surface_form).join('').trim();
    
    setSelectedSpan({
      startIdx,
      endIdx: newEndIdx,
      tokens: newTokens.filter(t => t.is_word),
      surfaceForm
    });
    
    // Note: We keep wordData cached so shrinking back to single word doesn't require re-fetch
    // The popover UI conditionally hides word-specific sections when isExpressionMode is true
  };

  const handleShrinkSelection = () => {
    if (!selectedSpan || selectedSpan.tokens?.length <= 1) return;
    
    // Shrink from the right
    const { startIdx, endIdx } = selectedSpan;
    
    // Find new end (previous word)
    let newEndIdx = endIdx - 1;
    while (newEndIdx > startIdx && !allTokens[newEndIdx]?.is_word) {
      newEndIdx--;
    }
    
    if (newEndIdx <= startIdx) {
      // Back to single word
      const token = allTokens[startIdx];
      setSelectedSpan({
        startIdx,
        endIdx: startIdx,
        tokens: [token],
        surfaceForm: token.surface_form?.trim() || ''
      });
      // Reload word data for single word
      handleReloadWordData(token);
      return;
    }
    
    // Build new span
    const newTokens = [];
    for (let i = startIdx; i <= newEndIdx; i++) {
      if (allTokens[i]) newTokens.push(allTokens[i]);
    }
    
    const surfaceForm = newTokens.map(t => t.surface_form).join('').trim();
    
    setSelectedSpan({
      startIdx,
      endIdx: newEndIdx,
      tokens: newTokens.filter(t => t.is_word),
      surfaceForm
    });
  };

  const handleReloadWordData = async (token) => {
    if (!token) return;
    const data = await loadEntry(token.normalized_form);
    if (data) {
      setNoteText(data.user_state?.notes || '');
      setTranslationText(data.user_state?.translation || '');
    }
  };

  // ---------- Actions ----------
  // Languages without Web Speech API support
  const TTS_UNSUPPORTED_LANGS = ['kea', 'ht']; // Cape Verdean Creole, Haitian Creole, etc.
  const isTTSSupported = !TTS_UNSUPPORTED_LANGS.includes(readerData?.language?.code);
  
  const handlePlayAudio = () => {
    if (!selectedToken) return;
    const word = selectedToken.surface_form?.trim() || selectedToken.normalized_form;
    if (!word) return;
    audioPreloader.playWord(word);
  };

  // Play full text using backend TTS (reliable across all browsers and languages)
  const fullTextAudioRef = useRef(null);

  const handlePlayFullText = (rateOverride) => {
    const rate = rateOverride ?? readerPlaybackRate;
    if (isPlayingFullText) {
      if (fullTextAudioRef.current) {
        fullTextAudioRef.current.pause();
        fullTextAudioRef.current = null;
      }
      setIsPlayingFullText(false);
      return;
    }

    const textContent = allTokens
      .map(t => t.surface_form)
      .join('')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 500); // /tts/speak max

    if (!textContent) {
      toast.error('No text content to read');
      return;
    }

    const langCode = readerData?.language?.code || 'en';
    const startTime = Date.now();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(textContent)}&lang=${encodeURIComponent(langCode)}`;

    const audio = new Audio(url);
    audio.crossOrigin = 'use-credentials';
    fullTextAudioRef.current = audio;
    audio.playbackRate = rate;

    audio.onplay = () => setIsPlayingFullText(true);
    audio.onended = () => {
      setIsPlayingFullText(false);
      fullTextAudioRef.current = null;
      const durationSeconds = (Date.now() - startTime) / 1000;
      if (readerData?.text?.text_id && durationSeconds > 5) {
        textsApi.trackListen(readerData.text.text_id, durationSeconds).catch(console.error);
      }
    };
    audio.onerror = (e) => {
      // MEDIA_ERR_ABORTED (code 1) fires when user stops audio — not a real error
      if (audio.error?.code === 1) return;
      setIsPlayingFullText(false);
      fullTextAudioRef.current = null;
      toast.error('Error playing audio');
    };

    audio.play().catch(() => {
      setIsPlayingFullText(false);
      toast.error('Error playing audio');
    });
  };

  // Stop TTS playback
  const handleStopPlayback = () => {
    if (fullTextAudioRef.current) { fullTextAudioRef.current.pause(); fullTextAudioRef.current = null; }
    if (sentenceAudioRef.current) { sentenceAudioRef.current.pause(); sentenceAudioRef.current = null; }
    setIsPlayingFullText(false);
    setIsPlayingSentence(false);
  };

  // Toggle sentence mode - view one sentence at a time
  const toggleSentenceMode = () => {
    const next = !sentenceMode;
    if (next) {
      pendingSentenceJumpRef.current = true;
      // Capture token index now so every subsequent measure() can find the right sentence,
      // even if ResizeObserver fires multiple times on mobile before React commits.
      sentenceJumpTokenRef.current = lastClickedTokenIdxRef.current ?? -1; // -1 = first sentence
    } else {
      sentenceJumpTokenRef.current = null;
    }
    setSentenceMode(next);
    handleStopPlayback();
  };

  // Navigate to next sentence
  const nextSentence = () => {
    handleStopPlayback();
    handlePageChange('next');
  };

  // Navigate to previous sentence
  const prevSentence = () => {
    handleStopPlayback();
    setCurrentPage(page => Math.max(0, page - 1));
  };

  // Sentence audio — uses backend TTS (reliable across all browsers and languages)
  const sentenceAudioRef = useRef(null);

  const playSentenceAudio = () => {
    const sentenceData = sentenceRanges[currentSentenceIndex];
    if (!sentenceData?.text) return;

    // Stop if already playing
    if (isPlayingSentence) {
      if (sentenceAudioRef.current) {
        sentenceAudioRef.current.pause();
        sentenceAudioRef.current = null;
      }
      setIsPlayingSentence(false);
      return;
    }

    const langCode = readerData?.language?.code || 'en';
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(sentenceData.text)}&lang=${encodeURIComponent(langCode)}`;

    const audio = new Audio(url);
    audio.crossOrigin = 'use-credentials';
    audio.playbackRate = readerPlaybackRate;
    sentenceAudioRef.current = audio;

    audio.onplay = () => setIsPlayingSentence(true);
    audio.onended = () => { setIsPlayingSentence(false); sentenceAudioRef.current = null; };
    audio.onerror = (e) => {
      if (audio.error?.code === 1) return; // MEDIA_ERR_ABORTED — user stopped, not a real error
      setIsPlayingSentence(false);
      sentenceAudioRef.current = null;
    };

    audio.play().catch(() => setIsPlayingSentence(false));
  };

  // Get tokens for current sentence in sentence mode
  const currentSentenceTokens = useMemo(() => {
    if (!sentenceMode || sentenceRanges.length === 0) return [];
    const range = sentenceRanges[currentSentenceIndex];
    if (!range) return [];
    return allTokens.slice(range.start, range.end);
  }, [sentenceMode, sentenceRanges, currentSentenceIndex, allTokens]);

  // Reset QA panel when sentence changes in sentence mode
  useEffect(() => {
    setSentenceTranslation(null);
    setShowQAPanel(false);
  }, [currentSentenceIndex, sentenceMode]);

  // Once currentPage matches the jump target, clear the jump ref so subsequent
  // measure() calls (resize, font-load) use normal anchor-based navigation.
  useEffect(() => {
    if (sentenceJumpTokenRef.current === null || !sentenceMode || !pagination.ready || !pagination.pages.length) return;
    if (!Object.prototype.hasOwnProperty.call(pagination.pages[0] || {}, 'sentenceIndex')) return;

    const tokenIdx = sentenceJumpTokenRef.current;
    let targetPage;
    if (tokenIdx === -1) {
      targetPage = 0;
    } else {
      const sentIdx = sentenceRanges.findIndex(r => tokenIdx >= r.start && tokenIdx < r.end);
      if (sentIdx < 0) return;
      targetPage = pagination.pages.findIndex(p => p.sentenceIndex === sentIdx);
      if (targetPage < 0) return;
    }

    if (currentPage === targetPage) {
      // Jump confirmed — release the override so future resizes navigate normally
      sentenceJumpTokenRef.current = null;
      pendingSentenceJumpRef.current = false;
    }
  }, [currentPage, sentenceMode, pagination.ready, pagination.pages, sentenceRanges]);

  // Translate the current sentence
  const handleTranslateSentence = async () => {
    if (sentenceTranslation) { setSentenceTranslation(null); return; }
    const text = currentSentenceTokens.map(t => t.surface_form || t.normalized_form || '').join('').trim();
    if (!text) return;
    const langCode = readerData?.language?.code || 'en';
    setSentenceTranslating(true);
    try {
      const res = await textsApi.translateSentence(text, langCode);
      const translation = res.data?.translation;
      if (translation) {
        setSentenceTranslation(translation);
      } else {
        toast.error('Translation unavailable');
      }
    } catch {
      toast.error('Translation failed');
    } finally {
      setSentenceTranslating(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (fullTextAudioRef.current) fullTextAudioRef.current.pause();
      if (sentenceAudioRef.current) sentenceAudioRef.current.pause();
    };
  }, []);

  // Track read progress (scroll position or time for non-scrolling texts)
  const readTrackedRef = useRef(false);
  const readStartTimeRef = useRef(null);
  
  useEffect(() => {
    if (!readerData?.text?.text_id) return;
    
    readStartTimeRef.current = Date.now();
    readTrackedRef.current = false;
    
    const trackReadIfNeeded = (progressPercent) => {
      if (!readTrackedRef.current && progressPercent >= 50) {
        readTrackedRef.current = true;
        textsApi.trackRead(readerData.text.text_id, progressPercent).catch(console.error);
      }
    };
    
    // For scrolling texts, track scroll position
    const handleScroll = () => {
      const textArea = document.querySelector('[data-testid="reader-text-area"]');
      if (!textArea) return;
      
      const scrollTop = textArea.scrollTop;
      const scrollHeight = textArea.scrollHeight - textArea.clientHeight;
      if (scrollHeight > 0) {
        const progress = (scrollTop / scrollHeight) * 100;
        trackReadIfNeeded(progress);
      }
    };
    
    // For non-scrolling texts (short ones), track time spent
    const timeCheckInterval = setInterval(() => {
      if (readTrackedRef.current) return;
      
      const textArea = document.querySelector('[data-testid="reader-text-area"]');
      if (!textArea) return;
      
      // Check if text fits without scrolling
      const isNonScrolling = textArea.scrollHeight <= textArea.clientHeight;
      if (isNonScrolling && !hasMoreTokens && totalPages <= 1) {
        const timeSpent = (Date.now() - readStartTimeRef.current) / 1000;
        if (timeSpent >= 10) {
          trackReadIfNeeded(50); // Mark as 50% read after 10 seconds
        }
      }
    }, 5000);
    
    // Also track via page navigation (paginated mode)
    if (totalPages > 1) {
      const pageProgress = ((activePage?.tokens.at(-1)?._readerIndex + 1) / (readerData.pagination?.total_tokens || allTokens.length)) * 100;
      trackReadIfNeeded(pageProgress);
    }
    
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      clearInterval(timeCheckInterval);
    };
  }, [readerData?.text?.text_id, readerData?.pagination?.total_tokens, currentPage, allTokens.length, totalPages, activePage, hasMoreTokens]);

  const handleSetStatus = async (status) => {
    if (!selectedToken) return;
    setActionLoading(true);
    try {
      const langCode = readerData?.language?.code || 'en';
      const prevStatus = selectedToken.status || 'new';
      await vocabularyApi.setWordStatus(selectedToken.normalized_form, langCode, status);

      // Push to undo stack
      setUndoStack(prev => [...prev.slice(-30), {
        word: selectedToken.normalized_form,
        langCode,
        prevStatus,
        newStatus: status,
        timestamp: Date.now()
      }]);

      // Map status to approximate strength for immediate UI update
      const strengthMap = { ignored: null, new: 0.0, learning: 0.2, familiar: 0.5, known: 0.8, mastered: 1.0 };
      const colorMap = { ignored: 'inherit', new: '#EF4444', learning: '#EAB308', familiar: '#22C55E', known: 'inherit', mastered: 'inherit' };
      const newStrength = strengthMap[status] ?? 0;
      const newColor = colorMap[status] || '#EF4444';

      setWordData((p) => ({ ...p, user_state: { ...p?.user_state, status, strength: newStrength } }));

      // Update all matching tokens in reader
      setReaderData((prev) => ({
        ...prev,
        tokens: prev.tokens.map((t) =>
          t.normalized_form === selectedToken.normalized_form
            ? { ...t, status, strength: newStrength, color: newColor }
            : t
        ),
      }));
      toast.success(`Word marked as ${status}`);
      closePopover();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Undo last status change
  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    try {
      await vocabularyApi.setWordStatus(last.word, last.langCode, last.prevStatus);
      const strengthMap = { ignored: null, new: 0.0, learning: 0.2, familiar: 0.5, known: 0.8, mastered: 1.0 };
      const colorMap = { ignored: 'inherit', new: '#EF4444', learning: '#EAB308', familiar: '#22C55E', known: 'inherit', mastered: 'inherit' };
      setReaderData(prev => ({
        ...prev,
        tokens: prev.tokens.map(t =>
          t.normalized_form === last.word
            ? { ...t, status: last.prevStatus, strength: strengthMap[last.prevStatus] ?? 0, color: colorMap[last.prevStatus] || '#EF4444' }
            : t
        ),
      }));
      setUndoStack(prev => prev.slice(0, -1));
      toast.success(`Undone: "${last.word}" → ${last.prevStatus}`);
    } catch {
      toast.error('Undo failed');
    }
  };

  const handleSaveTranslation = async () => {
    if (!wordData?.user_state?.state_id) {
      // If no state yet, set status to 'new' first to create a word state
      if (selectedToken) {
        try {
          const langCode = readerData?.language?.code || 'en';
          await vocabularyApi.setWordStatus(selectedToken.normalized_form, langCode, 'new');
          const res = await vocabularyApi.getWordData(selectedToken.normalized_form, langCode);
          setWordData(res.data);
          if (res.data?.user_state?.state_id) {
            await vocabularyApi.addTranslation(res.data.user_state.state_id, translationText);
            toast.success('Translation saved');
            setTranslationSaved(true);
            return;
          }
        } catch (err) {
          console.warn('Translation fallback failed:', err?.message || err);
        }
      }
      toast.error('Could not save — click a status button first');
      return;
    }
    setActionLoading(true);
    try {
      await vocabularyApi.addTranslation(wordData.user_state.state_id, translationText);
      toast.success('Translation saved');
      setTranslationSaved(true);
    } catch (err) {
      console.error('Failed to save translation:', err);
      toast.error('Failed to save translation');
    } finally {
      setActionLoading(false);
    }
  };

  // Auto-translate using context
  const handleAutoTranslate = async () => {
    if (!selectedToken) return;
    setActionLoading(true);
    try {
      const word = selectedToken.surface_form?.trim();
      const langCode = readerData?.language?.code || 'en';
      const langName = readerData?.language?.name || 'unknown';
      
      // Get context from surrounding tokens
      const tokenIdx = pageTokens.findIndex(t => t.token_id === selectedToken.token_id);
      const contextTokens = pageTokens.slice(Math.max(0, tokenIdx - 5), tokenIdx + 6);
      const context = contextTokens.map(t => t.surface_form?.trim()).filter(Boolean).join(' ');
      
      // Try to get translation via the enrichment API
      const res = await vocabularyApi.enrichWord(word, langCode, 'translation', true);
      if (res.data?.data?.translation) {
        setTranslationText(res.data.data.translation);
      } else if (res.data?.data?.definitions?.[0]?.definition) {
        // Fall back to first definition
        setTranslationText(res.data.data.definitions[0].definition);
      } else {
        toast.info('Opening Google Translate...');
        window.open(`https://translate.google.com/?sl=${langCode}&tl=en&text=${encodeURIComponent(word)}`, '_blank');
      }
    } catch (err) {
      console.error('Auto-translate error:', err);
      const word = selectedToken.surface_form?.trim();
      const langCode = readerData?.language?.code || 'en';
      window.open(`https://translate.google.com/?sl=${langCode}&tl=en&text=${encodeURIComponent(word)}`, '_blank');
    } finally {
      setActionLoading(false);
    }
  };

  // AI enrich: refresh a specific section or all
  const handleRefreshSection = async (section = null) => {
    // Expression Full Oracle mode — selectedToken is null; use expression enrichment instead
    if (expressionInFullOracle && selectedExpressionEntry) {
      await handleEnrichExpression();
      return;
    }

    if (!selectedToken) return;
    const langCode = readerData?.language?.code || 'en';
    
    // Check if trying to refresh a locked field
    const currentLockedFields = wordData?.locked_fields || [];
    if (section && currentLockedFields.includes(section)) {
      toast.error(`${section} is locked and cannot be overwritten by AI`);
      return;
    }
    
    try {
      toast.info(section ? `Refreshing ${section}...` : 'AI enriching...');
      const res = await vocabularyApi.enrichWord(selectedToken.normalized_form, langCode, section, true);
      const enriched = res.data?.data;
      const skippedLocked = res.data?.skipped_locked || [];
      
      if (enriched) {
        // Only update fields that are NOT locked
        const lockedFields = new Set(enriched.locked_fields || currentLockedFields);
        
        setWordData((prev) => {
          const updates = {};
          const listFields = ['ipa', 'definitions', 'synonyms', 'collocates', 'derivations', 'examples', 'related_expressions'];
          // Linguistic scalar/list fields that enrichment can also fill
          const linguisticFields = ['pos', 'lemma', 'morphology', 'inflection', 'ipa_variants', 'categories', 'romaji', 'translation_en'];
          for (const [k, v] of Object.entries(enriched)) {
            if (listFields.includes(k) || linguisticFields.includes(k)) {
              // Only update if field is NOT locked and the value is meaningful
              if (!lockedFields.has(k) && v !== null && v !== undefined) {
                updates[k] = v;
              }
            }
          }
          // Always update locked_fields list
          updates.locked_fields = Array.from(lockedFields);
          return { ...prev, ...updates };
        });
        
        if (skippedLocked.length > 0) {
          toast.info(`Skipped locked fields: ${skippedLocked.join(', ')}`);
        }
        toast.success(section ? `${section} refreshed` : 'Word enriched by AI');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI enrichment failed');
    }
  };

  const handleSaveNote = async (noteString) => {
    if (!selectedToken) return;
    setActionLoading(true);
    try {
      const langCode = readerData?.language?.code || 'en';
      const text = (noteString !== undefined && typeof noteString === 'string') ? noteString : noteText;
      await vocabularyApi.saveUserNote(selectedToken.normalized_form, langCode, text);
      toast.success('Note saved');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDefinition = (idx) => {
    setWordData((prev) => ({
      ...prev,
      definitions: (prev.definitions || []).filter((_, i) => i !== idx),
    }));
  };

  const handleUnlockWord = async () => {
    if (!selectedToken || !readerData?.language?.code) return;
    setActionLoading(true);
    try {
      await vocabularyApi.unlockWord(selectedToken.normalized_form, readerData.language.code);
      toast.success('Word unlocked');
      // Re-fetch word data to reflect unlocked state
      const res = await vocabularyApi.getWordData(selectedToken.normalized_form, readerData.language.code);
      setWordData(res.data);
    } catch {
      toast.error('Failed to unlock word');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveToLanguage = async () => {
    if (!selectedToken || !selectedLanguage) return;
    setActionLoading(true);
    try {
      const src = readerData?.language?.code || 'en';
      await vocabularyApi.saveWordToLanguage(selectedToken.normalized_form, src, selectedLanguage, markAsKnown);
      toast.success(`Word saved to ${selectedLanguage}${markAsKnown ? ' as known' : ''}`);
      setShowSaveLangDialog(false);
    } catch {
      toast.error('Failed to save word');
    } finally {
      setActionLoading(false);
    }
  };

  // Mastered celebration — called by oracle when a word's status transitions to mastered
  const handleMastered = (token) => {
    if (!token?.token_id) return;
    const norm = token.normalized_form;
    // Only show confetti once per word per session
    if (masteredWordsShownRef.current.has(norm)) return;
    masteredWordsShownRef.current.add(norm);

    // Update allTokens so the status bar immediately reflects the new mastered word
    setReaderData(prev => ({
      ...prev,
      tokens: (prev.tokens || []).map(t =>
        t.normalized_form === norm ? { ...t, status: 'mastered' } : t
      ),
    }));

    // Locate the word's DOM element and get its viewport position for the confetti burst
    const el = document.querySelector(`[data-token-id="${token.token_id}"]`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMasteredTokenInfo({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    setTimeout(() => setMasteredTokenInfo(null), 2500);
  };

  const handleTogglePreferred = async (defIndex) => {
    if (!selectedToken || !readerData?.language?.code) return;
    const langCode = readerData.language.code;
    const word = selectedToken.normalized_form;
    const isCurrentlyPreferred = wordData?.definitions?.[defIndex]?.preferred;

    try {
      // Ensure word state exists first
      if (!wordData?.user_state?.state_id) {
        await vocabularyApi.setWordStatus(word, langCode, 'new');
      }

      if (isCurrentlyPreferred) {
        await vocabularyApi.removePreferredDefinition(word, langCode, defIndex);
      } else {
        await vocabularyApi.setPreferredDefinition(word, langCode, defIndex);
      }
      // Update local state immediately
      setWordData((prev) => ({
        ...prev,
        definitions: (prev.definitions || []).map((d, i) =>
          i === defIndex ? { ...d, preferred: !isCurrentlyPreferred } : d
        ),
      }));
    } catch {
      toast.error('Failed to update preferred definition');
    }
  };

  // Handler for uploading word image (admin only)
  const handleUploadWordImage = async (file) => {
    // Expression Full Oracle mode — use dedicated expression image endpoint
    if (expressionInFullOracle && selectedExpressionEntry) {
      const entryId = selectedExpressionEntry.entry.entry_id;
      if (!entryId || !file) return;
      try {
        const res = await lexiconApi.uploadExpressionImage(entryId, file);
        toast.success('Image uploaded successfully');
        setSelectedExpressionEntry(prev => ({
          ...prev,
          entry: { ...prev.entry, image_url: res.data.image_url }
        }));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upload image');
      }
      return;
    }

    if (!selectedToken || !readerData?.language?.code || !file) return;
    const langCode = readerData.language.code;
    const word = selectedToken.normalized_form;

    try {
      const res = await vocabularyApi.uploadWordImage(word, langCode, file);
      toast.success('Image uploaded successfully');
      // Update local state with the returned URL
      setWordData((prev) => ({
        ...prev,
        image_url: res.data.image_url,
      }));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
    }
  };

  // Handler for removing word image (admin only)
  const handleRemoveWordImage = async () => {
    // Expression Full Oracle mode
    if (expressionInFullOracle && selectedExpressionEntry) {
      const entryId = selectedExpressionEntry.entry.entry_id;
      if (!entryId) return;
      try {
        await lexiconApi.removeExpressionImage(entryId);
        toast.success('Image removed');
        setSelectedExpressionEntry(prev => ({
          ...prev,
          entry: { ...prev.entry, image_url: null }
        }));
      } catch (err) {
        toast.error('Failed to remove image');
      }
      return;
    }

    if (!selectedToken || !readerData?.language?.code) return;
    const langCode = readerData.language.code;
    const word = selectedToken.normalized_form;

    try {
      await vocabularyApi.removeWordImage(word, langCode);
      toast.success('Image removed');
      setWordData((prev) => ({
        ...prev,
        image_url: null,
      }));
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  // Handler for admin direct edit of lexicon fields
  const handleSaveLexiconEdit = async (field, value) => {
    if (!selectedToken || !readerData?.language?.code) return;
    const langCode = readerData.language.code;
    const word = selectedToken.normalized_form;

    setActionLoading(true);
    try {
      // Update the shared lexicon directly (admin only)
      await lexiconApi.createOrUpdate({
        word,
        language_code: langCode,
        type: 'word',
        [field]: value,
      });
      
      // Update local state
      setWordData((prev) => ({
        ...prev,
        [field]: value,
        locked_fields: [...(prev.locked_fields || []), field], // Auto-lock edited field
      }));
      
      toast.success('Definition updated');
    } catch (err) {
      console.error('Failed to save edit:', err);
      toast.error('Failed to save changes');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler for user suggestion submission (non-admin)
  const handleSubmitSuggestion = async (field, value, reason = '') => {
    if (!selectedToken || !readerData?.language?.code) return;
    const langCode = readerData.language.code;
    const word = selectedToken.normalized_form;

    setActionLoading(true);
    try {
      // First, save as personal override so user sees their version immediately
      await lexiconApi.createOrUpdate({
        word,
        language_code: langCode,
        type: 'word',
        [field]: value, // This goes to user_overrides for non-admin
      });
      
      // If there's an entry_id, submit a suggestion for admin review
      if (wordData?.entry_id) {
        await lexiconApi.submitSuggestion(wordData.entry_id, {
          field,
          suggested_value: value,
          reason: reason || 'User correction',
        });
        toast.success('Suggestion submitted for review');
      } else {
        toast.success('Your correction has been saved');
      }
      
      // Update local state with user's version
      setWordData((prev) => ({
        ...prev,
        [field]: value,
      }));
    } catch (err) {
      console.error('Failed to submit suggestion:', err);
      toast.error('Failed to save suggestion');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler for toggling field lock (admin only)
  const handleToggleFieldLock = async (field) => {
    const w = wordData?.word || selectedToken?.word;
    const lc = wordData?.language_code || language?.code;
    if (!w || !lc) return;

    setActionLoading(true);
    try {
      const res = await lexiconApi.toggleFieldLock(w, lc, field);
      const newLocked = res.data?.locked_fields || [];
      setWordData((prev) => ({
        ...prev,
        locked_fields: newLocked,
      }));
      const action = res.data?.action || 'toggled';
      toast.success(`${field} ${action} — ${action === 'locked' ? 'protected from AI updates' : 'AI can update this field'}`);
    } catch (err) {
      console.error('Failed to toggle field lock:', err);
      toast.error('Failed to update field lock');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler for looking up a related word from within the popover (#12)
  const handleWordLookup = async (term) => {
    if (!term || !language?.code) return;
    setSelectedToken({ word: term, surface_form: term, normalized_form: term, is_word: true });
    setSelectedSpan(null);
    setNoteText('');
    setTranslationText('');
    const data = await loadEntry(term, term.includes(' '));
    if (data) {
      setNoteText(data.user_state?.notes || '');
      setTranslationText(data.user_state?.translation || '');
    }
  };

  // ---------- Edit / Delete ----------
  const openEditDialog = () => {
    if (readerData?.text) {
      setEditTitle(readerData.text.title || '');
      setEditContent(readerData.text.content || '');
      setEditDialogOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) { toast.error('Content cannot be empty'); return; }
    setSaving(true);
    try {
      await textsApi.update(textId, { title: editTitle.trim() || 'Untitled', content: editContent.trim() });
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 404
        ? 'This text no longer exists in the database. It may have been removed.'
        : status === 403
        ? (err?.response?.data?.detail || 'You do not have permission to edit this text.')
        : (err?.response?.data?.detail || err?.message || 'Failed to save');
      toast.error(msg);
      setSaving(false);
      return;
    }
    // Update local reader state immediately — no re-fetch needed to show success
    setReaderData(prev => prev ? {
      ...prev,
      text: { ...prev.text, title: editTitle.trim() || 'Untitled', content: editContent.trim(), is_tokenized: false }
    } : prev);
    setEditDialogOpen(false);
    toast.success('Text updated');
    setSaving(false);
    // Re-tokenize in background, then silently refresh tokens once done
    textsApi.tokenize(textId)
      .then(() => textsApi.getReaderData(textId))
      .then(res => {
        if (res?.data) {
          setReaderData(prev => prev ? {
            ...prev,
            tokens: res.data.tokens || prev.tokens,
            pagination: res.data.pagination || prev.pagination,
            text: { ...(prev.text || {}), ...(res.data.text || {}), is_tokenized: true },
          } : res.data);
        }
      })
      .catch(e => console.warn('Background re-tokenize/refresh failed:', e));
  };

  const handleDeleteText = async () => {
    setDeleting(true);
    try {
      await textsApi.delete(textId);
      toast.success('Text deleted');
      navigate('/dashboard');
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 403
        ? 'This is a shared/public text and cannot be deleted.'
        : 'Failed to delete';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Finish Text ----------
  const handleFinishText = async () => {
    setFinishLoading(true);
    try {
      const res = await textsApi.finishText(textId);
      setFinishStats(res.data);
      setShowFinishModal(true);
      // Refresh reader data to show updated statuses
      setReaderData(previous => ({ ...previous, tokens: markWordsKnown(previous.tokens, getNewPageWords(previous.tokens)) }));
      await fetchCompletionData();
      toast.success('Lesson completed!');
    } catch (err) {
      console.error('Failed to finish text:', err);
      toast.error('Failed to finish lesson');
    } finally {
      setFinishLoading(false);
    }
  };

  // Handle text selection for expressions
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      // Don't clear if single click (let token click handler work)
      return;
    }
    
    const selectedText = selection.toString().trim();
    if (!selectedText || !selectedText.includes(' ')) {
      // Single word or no space - not an expression
      setSelectedExpression(null);
      return;
    }
    
    // Try to find the tokens that match this selection
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    // Find all token elements within or containing the selection
    const readerContent = document.querySelector('[data-testid="reader-text-area"]')
      || document.querySelector('[data-testid="sentence-display"]');
    if (!readerContent) {
      // Fallback: just capture the text
      setSelectedExpression({ text: selectedText, tokens: [], range });
      return;
    }
    
    // Get all token elements
    const tokenElements = readerContent.querySelectorAll('.reader-token');
    const selectedTokens = [];
    
    tokenElements.forEach((el, idx) => {
      // Check if this element is within or intersects the selection
      const tokenRange = document.createRange();
      tokenRange.selectNodeContents(el);
      
      // Check if ranges intersect
      const intersects = (
        range.compareBoundaryPoints(Range.START_TO_END, tokenRange) > 0 &&
        range.compareBoundaryPoints(Range.END_TO_START, tokenRange) < 0
      );
      
      if (intersects) {
        // Get token data from data attribute or find in pageTokens
        const tokenId = el.getAttribute('data-token-id');
        const tokenIdx = parseInt(el.getAttribute('data-token-idx'), 10);
        
        if (!isNaN(tokenIdx) && pageTokens[tokenIdx]) {
          selectedTokens.push({
            ...pageTokens[tokenIdx],
            element: el,
            idx: tokenIdx
          });
        }
      }
    });
    
    // Filter to only word tokens
    const wordTokens = selectedTokens.filter(t => t.is_word);
    
    if (wordTokens.length >= 2) {
      // Valid multi-word expression — route to Oracle expression mode (same as arrow-key span)
      const surfaceForm = wordTokens.map(t => t.surface_form.trim()).join(' ');
      const startIdx = wordTokens[0].idx;
      const endIdx = wordTokens[wordTokens.length - 1].idx;

      setSelectedSpan({ startIdx, endIdx, tokens: wordTokens, surfaceForm });
      setSelectedToken(wordTokens[0]);

      // Position Oracle near the selection
      const rect = range.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const isMobile = viewportWidth < 768;
      const popoverWidth = isMobile ? Math.min(320, viewportWidth - 20) : 380;
      let left = rect.left + rect.width / 2 - popoverWidth / 2;
      left = Math.max(10, Math.min(left, viewportWidth - popoverWidth - 10));
      const top = Math.max(10, rect.top - 220);
      setPopoverPos({ top, left, isMobile, maxHeight: window.innerHeight * 0.7 });

      setSelectedExpression(null);
      window.getSelection()?.removeAllRanges();
    } else {
      setSelectedExpression(null);
    }
  }, [pageTokens]);

  // Listen for text selection
  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('touchend', handleTextSelection);
    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('touchend', handleTextSelection);
    };
  }, [handleTextSelection]);

  // Auto-translate span when expression mode opens (≥2 tokens)
  useEffect(() => {
    if (!selectedSpan || selectedSpan.tokens.length < 2) {
      setExpressionTranslation(null);
      return;
    }
    let cancelled = false;
    const surfaceText = selectedSpan.surfaceForm || selectedSpan.tokens.map(t => t.surface_form).join(' ');
    const langCode = readerData?.language?.code;
    textsApi.translateSentence(surfaceText, langCode)
      .then(r => { if (!cancelled) setExpressionTranslation(r.data?.translation || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedSpan, readerData?.language?.code]);

  // Save expression to backend
  const handleSaveExpression = useCallback(async () => {
    if (!selectedExpression) {
      toast.error('No expression selected');
      return;
    }
    
    // If tokens weren't mapped, create component words from the text
    let componentWords = selectedExpression.componentWords;
    if (!componentWords || componentWords.length === 0) {
      componentWords = selectedExpression.text.split(/\s+/).map(w => w.toLowerCase());
    }
    
    if (componentWords.length < 2) {
      toast.error('Select at least 2 words');
      return;
    }
    
    try {
      const { data } = await lexiconApi.createExpression({
        surface_form: selectedExpression.text,
        language_code: readerData?.language?.code,
        component_words: componentWords,
        text_id: readerData?.text?.text_id,
        context_sentence: null // Could extract sentence context in future
      });
      
      if (data.success) {
        if (data.action === 'created') {
          toast.success(`Expression "${selectedExpression.text}" saved!`);
        } else {
          toast.info(`Expression "${selectedExpression.text}" already exists`);
        }
        
        // Clear selection
        setSelectedExpression(null);
        window.getSelection()?.removeAllRanges();
      }
    } catch (err) {
      console.error('Failed to save expression:', err);
      toast.error('Failed to save expression');
    }
  }, [selectedExpression, readerData]);

  // Save expression from popover (using selectedSpan from arrow expansion)
  const handleSaveExpressionFromPopover = useCallback(async () => {
    if (!selectedSpan || selectedSpan.tokens?.length < 2) {
      toast.error('Select at least 2 words');
      return;
    }
    
    setExpressionSaving(true);
    try {
      const componentWords = selectedSpan.tokens
        .filter(t => t.is_word)
        .map(t => t.normalized_form);
      
      const { data } = await lexiconApi.createExpression({
        surface_form: selectedSpan.surfaceForm,
        language_code: readerData?.language?.code,
        component_words: componentWords,
        text_id: readerData?.text?.text_id,
        context_sentence: null
      });
      
      if (data.success) {
        if (data.action === 'created') {
          toast.success(`Expression "${selectedSpan.surfaceForm}" saved!`);
        } else {
          toast.info(`Expression already exists`);
        }
        // Refresh expression detection
        detectExpressionsInText();
      }
    } catch (err) {
      console.error('Failed to save expression:', err);
      toast.error('Failed to save expression');
    } finally {
      setExpressionSaving(false);
    }
  }, [selectedSpan, readerData, detectExpressionsInText]);

  // Enrich expression from popover (save first, then enrich)
  const handleEnrichExpressionFromPopover = useCallback(async () => {
    if (!selectedSpan || selectedSpan.tokens?.length < 2) {
      toast.error('Select at least 2 words');
      return;
    }
    
    setExpressionSaving(true);
    try {
      const componentWords = selectedSpan.tokens
        .filter(t => t.is_word)
        .map(t => t.normalized_form);
      
      // First create the expression
      const { data: createData } = await lexiconApi.createExpression({
        surface_form: selectedSpan.surfaceForm,
        language_code: readerData?.language?.code,
        component_words: componentWords,
        text_id: readerData?.text?.text_id,
        context_sentence: null
      });
      
      if (createData.success && createData.entry?.entry_id) {
        // Now enrich it
        const { data: enrichData } = await lexiconApi.enrichExpression(createData.entry.entry_id);
        if (enrichData.success) {
          toast.success('Expression saved and enriched with AI!');
        } else {
          toast.success('Expression saved! (AI enrichment pending)');
        }
        // Refresh expression detection
        detectExpressionsInText();
      }
    } catch (err) {
      console.error('Failed to save/enrich expression:', err);
      toast.error('Failed to process expression');
    } finally {
      setExpressionSaving(false);
    }
  }, [selectedSpan, readerData, detectExpressionsInText]);

  // Save span as Verb Formation
  const handleSaveVerbFormationFromPopover = useCallback(async () => {
    if (!selectedSpan || selectedSpan.tokens?.length < 2) {
      toast.error('Select at least 2 words for a verb formation');
      return;
    }
    setVerbFormationSaving(true);
    try {
      const wordTokens = selectedSpan.tokens.filter(t => t.is_word);
      const components = wordTokens.map(t => t.normalized_form);
      // Guess verb element: last word token is usually the verb in Persian/Japanese/Korean
      const verbEl = wordTokens.length ? wordTokens[wordTokens.length - 1].normalized_form : '';
      const { data } = await lexiconApi.createVerbFormation({
        surface_form: selectedSpan.surfaceForm,
        language_code: readerData?.language?.code,
        components,
        verb_element: verbEl,
        text_id: readerData?.text?.text_id,
      });
      if (data.success) {
        toast.success(data.action === 'created'
          ? `Verb formation "${selectedSpan.surfaceForm}" saved!`
          : `Verb formation already exists`);
        detectVerbFormationsInText();
        // Open verb formation oracle
        setSelectedVerbFormationEntry({ entry: data.entry, user_state: { status: 'new' } });
        setVerbFormationInFullOracle(true);
        setSelectedToken(null);
        setSelectedSpan(null);
      }
    } catch {
      toast.error('Failed to save verb formation');
    } finally {
      setVerbFormationSaving(false);
    }
  }, [selectedSpan, readerData, detectVerbFormationsInText]);

  // Delete Expression from full oracle
  const handleDeleteExpression = useCallback(async () => {
    if (!selectedExpressionEntry?.entry?.entry_id) return;
    if (!window.confirm(`Delete expression "${selectedExpressionEntry.entry.word}"?`)) return;
    try {
      await lexiconApi.deleteExpression(selectedExpressionEntry.entry.entry_id);
      toast.success('Expression deleted');
      setExpressionInFullOracle(false);
      setSelectedExpressionEntry(null);
      detectExpressionsInText();
    } catch {
      toast.error('Failed to delete expression');
    }
  }, [selectedExpressionEntry, detectExpressionsInText]);

  // Edit Expression from full oracle
  const handleUpdateExpression = useCallback(async (updatedFields) => {
    if (!selectedExpressionEntry?.entry?.entry_id) return;
    try {
      const { data } = await lexiconApi.updateExpression(selectedExpressionEntry.entry.entry_id, updatedFields);
      if (data.success) {
        setSelectedExpressionEntry(prev => ({ ...prev, entry: data.entry }));
        toast.success('Expression updated');
      }
    } catch {
      toast.error('Failed to update expression');
    }
  }, [selectedExpressionEntry]);

  // Delete Verb Formation from full oracle
  const handleDeleteVerbFormation = useCallback(async () => {
    if (!selectedVerbFormationEntry?.entry?.entry_id) return;
    if (!window.confirm(`Delete verb formation "${selectedVerbFormationEntry.entry.word}"?`)) return;
    try {
      await lexiconApi.deleteVerbFormation(selectedVerbFormationEntry.entry.entry_id);
      toast.success('Verb formation deleted');
      setVerbFormationInFullOracle(false);
      setSelectedVerbFormationEntry(null);
      detectVerbFormationsInText();
    } catch {
      toast.error('Failed to delete verb formation');
    }
  }, [selectedVerbFormationEntry, detectVerbFormationsInText]);

  // Edit Verb Formation from full oracle
  const handleUpdateVerbFormation = useCallback(async (updatedFields) => {
    if (!selectedVerbFormationEntry?.entry?.entry_id) return;
    try {
      const { data } = await lexiconApi.updateVerbFormation(selectedVerbFormationEntry.entry.entry_id, updatedFields);
      if (data.success) {
        setSelectedVerbFormationEntry(prev => ({ ...prev, entry: data.entry }));
        toast.success('Verb formation updated');
      }
    } catch {
      toast.error('Failed to update verb formation');
    }
  }, [selectedVerbFormationEntry]);

  // Open a related formation by surface form — auto-save if not existing, then show oracle
  const handleOpenRelatedFormation = useCallback(async (surfaceForm) => {
    if (!surfaceForm || !readerData?.language?.code) return;
    try {
      const { data } = await lexiconApi.createVerbFormation({
        surface_form: surfaceForm,
        language_code: readerData.language.code,
        components: surfaceForm.split(/\s+/),
      });
      if (data.success) {
        setSelectedVerbFormationEntry({ entry: data.entry, user_state: { status: 'new' } });
        setVerbFormationInFullOracle(true);
        setExpressionInFullOracle(false);
        setSelectedToken(null);
      }
    } catch {
      toast.error('Could not open related formation');
    }
  }, [readerData]);

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!readerData) {
    if (textError === 'not_found') {
      return (
        <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
          <div className="text-center space-y-4 max-w-sm px-6">
            <div className="text-5xl">📄</div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Text Not Found</h2>
            <p className="text-[var(--text-secondary)] text-sm">
              This text no longer exists or may have been removed. It won't cause any further errors.
            </p>
            <button
              onClick={() => navigate('/library')}
              className="px-4 py-2 bg-[var(--accent)] text-white rounded-lg text-sm hover:opacity-90 transition-opacity"
            >
              Back to Library
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  const { text, language, tokens } = readerData;
  const direction = getTextDirection(language);
  const isRTLText = isRTL(language);
  const isChineseText = isChinese(language);

  // Stats — exclude ignored words from all counts
  const wordTokens = tokens.filter((t) => t.is_word);
  const ignoredWords = new Set();
  wordTokens.forEach((t) => { if (t.status === 'ignored') ignoredWords.add(t.normalized_form); });
  const uniqueWords = new Set(wordTokens.map((t) => t.normalized_form));
  const activeUniqueWords = new Set([...uniqueWords].filter(w => !ignoredWords.has(w)));
  const totalUniqueWords = activeUniqueWords.size;
  const knownUniqueWords = new Set();
  wordTokens.forEach((t) => {
    if (!ignoredWords.has(t.normalized_form) && ((t.strength || 0) >= 0.8 || t.status === 'known')) knownUniqueWords.add(t.normalized_form);
  });
  const knownCount = knownUniqueWords.size;
  const unknownCount = totalUniqueWords - knownCount;
  const knownPct = totalUniqueWords > 0 ? (knownCount / totalUniqueWords) * 100 : 0;

  // Learning progress — words that are no longer "new" (have been interacted with)
  const learnedWords = new Set();
  wordTokens.forEach((t) => {
    if (!ignoredWords.has(t.normalized_form) && t.status && t.status !== 'new') {
      learnedWords.add(t.normalized_form);
    }
  });
  const learnPct = totalUniqueWords > 0 ? Math.round((learnedWords.size / totalUniqueWords) * 100) : 0;

  // Flashcard from Reader
  const handleOpenFlashcardModal = async () => {
    setShowFlashcardModal(true);
    try {
      const res = await vocabularyApi.getFlashcardLists();
      setFlashcardLists(res.data || []);
    } catch { setFlashcardLists([]); }
  };
  const handleConfirmFlashcard = async () => {
    if (!selectedToken) return;
    setSavingFlashcard(true);
    try {
      let listId = selectedFlashcardList;
      if (newFlashcardListName.trim()) {
        const langCode = readerData?.language?.code || '';
        const res = await vocabularyApi.createFlashcardList({ name: newFlashcardListName.trim(), language_code: langCode });
        listId = res.data?.list_id;
      }
      if (!listId) { toast.error('Select or create a list'); setSavingFlashcard(false); return; }
      await vocabularyApi.addToFlashcardList(listId, {
        word: selectedToken.normalized_form || selectedToken.surface_form,
        language_code: readerData?.language?.code || '',
        definitions: wordData?.definitions || [],
      });
      toast.success('Added to flashcards');
      setShowFlashcardModal(false);
      setNewFlashcardListName(''); setSelectedFlashcardList('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add flashcard');
    } finally { setSavingFlashcard(false); }
  };

  return (
    <div
      className="mx-auto reader-container relative"
      style={{
        maxWidth: readerWidth > 0 ? `${readerWidth}px` : '56rem',
      }}
      data-testid="reader-container"
    >
      {/* Grabbable right edge (#17) */}
      <div
        className="absolute top-0 right-0 w-2 h-full cursor-ew-resize z-30 group hidden md:block"
        onMouseDown={handleResizeStart}
        title="Drag to resize Reader width"
        data-testid="reader-resize-handle"
      >
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 rounded-full bg-border/50 group-hover:bg-primary/50 transition-colors" />
      </div>
      {/* Header - Title Row */}
      <div className={`flex items-center gap-3 shrink-0 ${isRTLText ? 'flex-row-reverse' : ''}`} data-testid="reader-header">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="shrink-0" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className={`flex-1 min-w-0 ${isRTLText ? 'text-right' : ''}`}>
          <h1 className={`text-lg md:text-xl font-semibold truncate ${isArabicScript(language) ? 'font-arabic' : ''}`} dir={direction} data-testid="reader-title">
            {text.title}
          </h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs font-medium text-primary/80">{language?.name}</span>
            <span className="text-xs text-muted-foreground">{totalUniqueWords} words</span>
          </div>
        </div>
      </div>

      {/* Tools Row */}
      <ReaderControls>
      <div className="flex items-center justify-between flex-wrap gap-1.5 pb-2 border-b border-border/50 shrink-0" data-testid="reader-tools">
        <div className="flex items-center gap-1 flex-wrap">
          {/* TTS Controls */}
          <div className="relative flex items-center gap-1 border border-border rounded-md px-1" data-testid="tts-controls">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7" 
              onClick={() => {
                if (isPlayingFullText) {
                  handlePlayFullText(); // stops
                } else {
                  setShowReaderSpeedPicker(p => !p);
                }
              }}
              title={isPlayingFullText ? "Stop reading" : "Read full text aloud"}
              data-testid="play-full-text-btn"
            >
              {isPlayingFullText ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            </Button>
            {showReaderSpeedPicker && !isPlayingFullText && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2 min-w-[110px]" data-testid="reader-speed-picker">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-center mb-1.5">Speed</div>
                {[
                  { label: 'Normal', rate: 1.0, pct: '100%' },
                  { label: 'Slow',   rate: 0.8, pct: '80%'  },
                  { label: 'Slower', rate: 0.7, pct: '70%'  },
                ].map(({ label, rate, pct }) => (
                  <button
                    key={rate}
                    onClick={() => { setReaderPlaybackRate(rate); setShowReaderSpeedPicker(false); handlePlayFullText(rate); }}
                    className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-colors hover:bg-slate-700 ${readerPlaybackRate === rate ? 'text-amber-400 font-semibold' : 'text-slate-300'}`}
                    data-testid={`reader-speed-${label.toLowerCase()}-btn`}
                  >
                    <span>{label}</span>
                    <span className="text-slate-500">{pct}</span>
                  </button>
                ))}
              </div>
            )}
            <Button 
              variant={sentenceMode ? "secondary" : "ghost"}
              size="icon" 
              className="h-7 w-7" 
              onClick={toggleSentenceMode}
              title={sentenceMode ? "View full text" : "View sentence by sentence"}
              data-testid="sentence-mode-btn"
            >
              {sentenceMode ? <Type className="h-3 w-3" /> : <AlignJustify className="h-3 w-3" />}
            </Button>
            <span className="hidden sm:inline text-[11px] text-muted-foreground">{sentenceMode ? 'Full text' : 'Sentence'}</span>
            {audioPreloader.preloading && (
              <span className="text-[10px] text-muted-foreground animate-pulse" data-testid="audio-preloading" title={`${audioPreloader.preloadCount} words cached`}>
                <Volume2 className="h-3 w-3 inline-block mr-0.5" />
                loading...
              </span>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={openEditDialog} className="h-8 w-8 sm:h-auto sm:w-auto sm:gap-1 sm:px-3" data-testid="edit-btn">
            <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Edit</span>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-auto sm:w-auto sm:gap-1 sm:px-3 text-destructive hover:text-destructive" data-testid="delete-btn">
                <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">Delete</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this text?</AlertDialogTitle>
                <AlertDialogDescription>Your vocabulary progress will be preserved.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteText} disabled={deleting} className="bg-destructive">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="ghost" size="sm" onClick={() => setShowStats(!showStats)} data-testid="toggle-stats-btn">
            {showStats ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} <span className="hidden sm:inline">{showStats ? 'Hide Stats' : 'Show Stats'}</span>
          </Button>
          <div className="flex items-center gap-1 border border-border rounded-md px-1" data-testid="font-size-controls">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 min-w-[36px] min-h-[36px] touch-manipulation" 
              onClick={() => setFontSize(f => { const v = Math.max(f - 2, 12); localStorage.setItem('iling-reader-font-size', v); return v; })} 
              data-testid="font-decrease-btn" 
              title="Decrease font size"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground w-8 text-center font-medium">{fontSize}</span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 min-w-[36px] min-h-[36px] touch-manipulation" 
              onClick={() => setFontSize(f => { const v = Math.min(f + 2, 32); localStorage.setItem('iling-reader-font-size', v); return v; })} 
              data-testid="font-increase-btn" 
              title="Increase font size"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Simplify / Complexify (#21) */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1 text-muted-foreground hover:text-blue-400"
              onClick={async () => {
                const title = readerData?.text?.title || 'Text';
                if (!window.confirm(`Generate a simplified version of "${title}"?\n\nIt will be saved as "${title} — Simplified".`)) return;
                try {
                  toast.info('Generating simpler version...');
                  const res = await textsApi.generateAdaptive(textId, 'i-1');
                  toast.success('Simplified version created!');
                  navigate(`/reader/${res.data.text_id}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to simplify');
                }
              }}
              data-testid="simplify-btn"
              title="Generate a simpler version of this text"
            >
              <ArrowDown className="h-3.5 w-3.5" />
              Simplify
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1 text-muted-foreground hover:text-purple-400"
              onClick={async () => {
                const title = readerData?.text?.title || 'Text';
                if (!window.confirm(`Generate a more complex version of "${title}"?\n\nIt will be saved as "${title} — Enhanced".`)) return;
                try {
                  toast.info('Generating more complex version...');
                  const res = await textsApi.generateAdaptive(textId, 'i+1');
                  toast.success('More complex version created!');
                  navigate(`/reader/${res.data.text_id}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to generate');
                }
              }}
              data-testid="complexify-btn"
              title="Generate a more complex version of this text"
            >
              <ArrowUp className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Increment</span><span className="sm:hidden">i+1</span>
            </Button>
          </div>
          {/* Undo */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1 text-muted-foreground hover:text-amber-400"
              onClick={handleUndo}
              disabled={undoStack.length === 0 || actionLoading}
              title={undoStack.length > 0 ? `Undo: "${undoStack[undoStack.length - 1]?.word}" → ${undoStack[undoStack.length - 1]?.prevStatus}` : 'Nothing to undo'}
              data-testid="undo-btn"
            >
              <Undo2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Undo</span>
              {undoStack.length > 0 && <span className="text-[10px] opacity-60">{undoStack.length}</span>}
            </Button>
          </div>
        </div>
      </div>

      {/* Comprehension Bar — whole-text progress, unique words, always visible */}
      {readerData?.tokens && (() => {
        // Always use allTokens so pagination/sentence mode don't affect the count.
        // Deduplicate by normalized_form so each unique word counts once.
        const seen = new Set();
        const wordToks = (allTokens || []).filter(t => {
          if (!t.is_word || !t.normalized_form) return false;
          if (seen.has(t.normalized_form)) return false;
          seen.add(t.normalized_form);
          return true;
        });
        const total = wordToks.length;
        if (total === 0) return null;
        const counts = { new: 0, learning: 0, familiar: 0, known: 0, mastered: 0, ignored: 0 };
        wordToks.forEach(t => {
          const s = t.status || 'new';
          counts[s] = (counts[s] || 0) + 1;
        });
        const comprehendedPct = Math.round(((counts.known + counts.mastered + counts.ignored + counts.familiar) / total) * 100);
        const segments = [
          { key: 'mastered', color: '#8B5CF6', count: counts.mastered, label: 'Mastered' },
          { key: 'known',    color: 'hsl(var(--foreground))', count: counts.known,    label: 'Known' },
          { key: 'familiar', color: '#22C55E', count: counts.familiar, label: 'Familiar' },
          { key: 'learning', color: '#EAB308', count: counts.learning, label: 'Learning' },
          { key: 'new',      color: '#EF4444', count: counts.new,      label: 'New' },
        ];
        return (
          <div className="shrink-0 space-y-1" data-testid="comprehension-bar">
            <div className="flex h-2 flex-1 rounded-full overflow-hidden bg-muted/30">
              {segments.map(({ key, color, count }) => {
                const pct = (count / total) * 100;
                if (pct === 0) return null;
                return <div key={key} style={{ width: `${pct}%`, backgroundColor: color }} className="transition-all duration-300" title={`${key}: ${count} (${Math.round(pct)}%)`} />;
              })}
            </div>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <div className="flex gap-2.5 flex-wrap">
                {segments.map(({ key, color, count, label }) => count > 0 && (
                  <span key={key} className="flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                    {label} {count}
                  </span>
                ))}
              </div>
              <span className="font-mono whitespace-nowrap">{comprehendedPct}% full text</span>
            </div>
          </div>
        );
      })()}

      {/* Enrichment bar — shown during Phase 1 (priority) and Phase 2 (full background) */}
      {showStats && enrichProgress && !enrichProgress.not_found && !enrichProgress.complete && (
        (() => {
          const done = enrichProgress.done ?? 0;
          const total = enrichProgress.total ?? 1;
          const pct = Math.round((done / Math.max(total, 1)) * 100);
          const newCount = enrichProgress.enriched ?? 0;
          const currentWord = enrichProgress.current_word || '';
          return (
            <div
              className="shrink-0 rounded-lg px-4 py-2.5 space-y-1.5"
              style={{ background: 'rgba(10,12,18,0.92)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid="canonization-bar"
            >
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  <Sparkles className="h-3 w-3 animate-pulse text-amber-400" />
                  <span>AI enrichment running…</span>
                  {currentWord && (
                    <span className="font-mono text-[10px] text-amber-300/70 truncate max-w-[120px]">{currentWord}</span>
                  )}
                </div>
                <span className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {done}/{total}
                  {newCount > 0 && <span className="text-amber-400 ml-1.5">+{newCount} new</span>}
                </span>
              </div>
              <div className="relative h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              </div>
            </div>
          );
        })()
      )}

      {/* Grammar Tips — collapsible, based on text language */}
      {language?.code && !grammarDismissed && (
        <GrammarRecommendations
          type="after-text"
          languageCode={language.code}
          limit={3}
          showDismiss
          defaultCollapsed
          collapsed={grammarCollapsed}
          onCollapsedChange={setGrammarCollapsed}
          onDismiss={() => setGrammarDismissed(true)}
          className="shrink-0"
        />
      )}

      {/* Generated Audio Player - shows when text has AI-generated audio */}
      {(text.audio_female_url || text.audio_male_url) && (
        <Card className="bg-muted/30" data-testid="audio-player-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Generated Audio</span>
            </div>
            <AudioPlayer
              textId={text.text_id}
              audioFemaleUrl={text.audio_female_url}
              audioMaleUrl={text.audio_male_url}
              hasSentenceAudio={text.has_sentence_audio}
              sentenceCount={text.sentence_count || 0}
              onPlayStart={() => {
                // Track listening when generated audio plays
                const startTime = Date.now();
                window._audioStartTime = startTime;
              }}
              onPlayEnd={() => {
                // Track duration when audio ends
                if (window._audioStartTime) {
                  const duration = (Date.now() - window._audioStartTime) / 1000;
                  if (duration > 5) {
                    textsApi.trackListen(text.text_id, duration).catch(console.error);
                  }
                  delete window._audioStartTime;
                }
              }}
              compact={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      </ReaderControls>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit Text</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="edit-title-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Content</label>
              {isChineseText && <p className="text-xs text-muted-foreground">Add spaces between words you want to learn separately</p>}
              <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="min-h-[300px] font-reader" dir={direction} data-testid="edit-content-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reader Content */}
      <Card className="reader-reading-card" data-testid="reader-card">
        <div className="reader-reading-viewport" ref={textContainerRef} data-testid="reader-card-content" data-pagination-ready={pagination.ready}>
          <div
            ref={readerRef}
            className={`reader-page-text reader-text font-reader ${isArabicScript(language) ? 'font-arabic' : ''}`}
            dir={direction}
            data-testid={sentenceMode ? 'sentence-display' : 'reader-text-area'}
            style={{ fontSize: `${fontSize + (sentenceMode ? 4 : 0)}px`, textAlign: sentenceMode ? 'center' : 'justify' }}
          >
            <ReaderTokens tokens={pageTokens} allTokens={allTokens} sentenceMode={sentenceMode} selectedToken={selectedToken} expressionMap={expressionMap} verbFormationMap={verbFormationMap} hoveredExpressionIdx={hoveredExpressionIdx} selectedExpressionEntry={selectedExpressionEntry} selectedVerbFormationEntry={selectedVerbFormationEntry} onWordClick={handleTokenClick} onExpressionClick={handleExpressionClick} onExpressionHover={setHoveredExpressionIdx} />
          </div>
          {pagination.insufficientSpace && <p className="text-xs text-muted-foreground" role="status" data-testid="reader-space-warning">Make the reading area larger or reduce the font size to continue.</p>}
        </div>
      </Card>

      <div className="reader-measure-root" aria-hidden="true" inert="">
        <div ref={measurementTemplateRef} className={`reader-page-text reader-text font-reader ${isArabicScript(language) ? 'font-arabic' : ''}`} dir={direction} style={{ fontSize: `${fontSize + (sentenceMode ? 4 : 0)}px`, textAlign: sentenceMode ? 'center' : 'justify' }}>
          <ReaderTokens tokens={pagination.geometryTokens} allTokens={pagination.geometryTokens} sentenceMode={sentenceMode} expressionMap={expressionMap} measuring />
        </div>
      </div>

      {/* Single floating popover - rendered in portal to escape transform context */}
      {(selectedToken || expressionInFullOracle || verbFormationInFullOracle) && popoverPos && createPortal(
        <WordPopoverErrorBoundary top={popoverPos.top} left={popoverPos.left} onClose={closePopover}>
          <WordPopoverPanel
          style={{ 
            position: 'fixed',
            top: popoverPos.top, 
            left: popoverPos.left,
            maxHeight: popoverPos.maxHeight,
          }}
          wordData={
            verbFormationInFullOracle && selectedVerbFormationEntry ? {
              ...selectedVerbFormationEntry.entry,
              user_state: selectedVerbFormationEntry.user_state,
              entry_id: selectedVerbFormationEntry.entry?.entry_id,
              language_code: readerData?.language?.code,
            } :
            expressionInFullOracle && selectedExpressionEntry ? {
              ...selectedExpressionEntry.entry,
              user_state: selectedExpressionEntry.user_state,
              entry_id: selectedExpressionEntry.entry?.entry_id,
              language_code: readerData?.language?.code,
            } : wordData
          }
          wordLoading={(expressionInFullOracle || verbFormationInFullOracle) ? false : wordLoading}
          token={selectedToken}
          language={language}
          noteText={noteText}
          setNoteText={setNoteText}
          translationText={translationText}
          setTranslationText={setTranslationText}
          translationSaved={translationSaved}
          onPlayAudio={handlePlayAudio}
          isTTSSupported={isTTSSupported}
          onSetStatus={handleSetStatus}
          onSaveTranslation={handleSaveTranslation}
          onAutoTranslate={handleAutoTranslate}
          onSaveNote={handleSaveNote}
          onOpenSaveLang={() => {
            // Hide the popover UI but keep selectedToken for the dialog
            setPopoverPos(null);
            setShowSaveLangDialog(true);
          }}
          onClose={closePopover}
          onRefreshSection={handleRefreshSection}
          onDeleteDefinition={handleDeleteDefinition}
          onTogglePreferred={handleTogglePreferred}
          onSaveEdit={handleSaveLexiconEdit}
          onSubmitSuggestion={handleSubmitSuggestion}
          onToggleFieldLock={handleToggleFieldLock}
          actionLoading={actionLoading}
          locked={wordData?.locked === true}
          lockedFields={wordData?.locked_fields || []}
          isMobile={popoverPos.isMobile}
          isAdmin={user?.is_admin === true}
          onUnlockWord={handleUnlockWord}
          onUploadImage={handleUploadWordImage}
          onRemoveImage={handleRemoveWordImage}
          // Expression support
          selectedSpan={selectedSpan}
          isExpressionMode={isExpressionMode}
          onExpandLeft={handleExpandLeft}
          onExpandRight={handleExpandRight}
          onShrinkSelection={handleShrinkSelection}
          onSaveExpression={handleSaveExpressionFromPopover}
          onEnrichExpression={expressionInFullOracle ? handleEnrichExpression : handleEnrichExpressionFromPopover}
          expressionSaving={expressionSaving}
          expressionTranslation={expressionTranslation}
          isExpressionInFullOracle={expressionInFullOracle}
          onOpenExpressionPanel={() => setExpressionInFullOracle(false)}
          // Verb formation support
          onSaveVerbFormation={handleSaveVerbFormationFromPopover}
          verbFormationSaving={verbFormationSaving}
          isVerbFormationInFullOracle={verbFormationInFullOracle}
          onOpenVerbFormationPanel={() => setVerbFormationInFullOracle(false)}
          onEnrichVerbFormation={verbFormationInFullOracle && selectedVerbFormationEntry
            ? async () => {
                const { data } = await lexiconApi.enrichVerbFormation(selectedVerbFormationEntry.entry.entry_id);
                if (data.success) {
                  setSelectedVerbFormationEntry(prev => ({ ...prev, entry: data.entry }));
                  toast.success('Verb formation enriched!');
                }
              }
            : undefined}
          // Edit / Delete handlers
          onDeleteExpression={expressionInFullOracle ? handleDeleteExpression : undefined}
          onUpdateExpression={expressionInFullOracle ? handleUpdateExpression : undefined}
          onDeleteVerbFormation={verbFormationInFullOracle ? handleDeleteVerbFormation : undefined}
          onUpdateVerbFormation={verbFormationInFullOracle ? handleUpdateVerbFormation : undefined}
          onOpenRelatedFormation={handleOpenRelatedFormation}
          onAddFlashcard={handleOpenFlashcardModal}
          onWordLookup={handleWordLookup}
          onDragHandleMouseDown={handleOracleDragStart}
          sentenceText={sentenceContext}
          userTier={user?.tier || 1}
          onMastered={handleMastered}
          />
        </WordPopoverErrorBoundary>,
        document.body
      )}

      {/* Mastered celebration confetti - fires at the word's position in the reader */}
      {masteredTokenInfo && createPortal(
        <div
          className="reader-confetti-burst"
          style={{ left: masteredTokenInfo.x, top: masteredTokenInfo.y }}
        >
          {READER_CONFETTI_PARTICLES.map((p, i) => (
            <div
              key={i}
              className="reader-confetti-particle"
              style={{
                '--tx': `${p.tx}px`,
                '--ty': `${p.ty}px`,
                width: p.size,
                height: p.height,
                borderRadius: p.isCircle ? '50%' : '2px',
                backgroundColor: p.color,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
          <div className="reader-mastered-label">★ MASTERED ★</div>
        </div>,
        document.body
      )}

      {/* Expression Popover - for saved expressions/idioms */}
      {selectedExpressionEntry && popoverPos && !expressionInFullOracle && createPortal(
        <div
          className="expression-popover-panel z-50 rounded-xl shadow-2xl border border-purple-500/30 bg-background/95 backdrop-blur-sm"
          data-popover-panel
          style={{ 
            position: 'fixed',
            top: popoverPos.top, 
            left: popoverPos.left,
            maxHeight: popoverPos.maxHeight,
            width: popoverPos.isMobile ? 'calc(100vw - 20px)' : '380px',
          }}
          data-testid="expression-popover"
        >
          <div className="flex flex-col h-full max-h-[inherit] overflow-hidden">
            {/* Drag handle */}
            <div
              onMouseDown={handleOracleDragStart}
              onTouchStart={handleOracleDragStart}
              className="flex items-center justify-center w-full select-none"
              style={{ height: 20, cursor: 'grab', flexShrink: 0 }}
              title="Drag to reposition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg>
            </div>
            {/* Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-purple-400" />
                  <span className="text-xs text-purple-400 font-medium">EXPRESSION</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpressionInFullOracle(true)}
                    className="text-xs text-purple-400 hover:text-purple-300 border border-purple-500/40 hover:border-purple-400/60 rounded px-2 py-0.5 transition-colors"
                    data-testid="open-in-full-oracle-btn"
                    title="Open in Full Oracle"
                  >
                    Full Oracle
                  </button>
                  <button 
                    onClick={closeExpressionPopover}
                    className="text-muted-foreground hover:text-foreground ml-1"
                    data-testid="close-expression-popover"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <h2 className="text-xl font-bold mt-2" data-testid="expression-title">
                {selectedExpressionEntry.entry.word}
              </h2>
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-muted-foreground mt-1">
                  {selectedExpressionEntry.entry.component_words?.length} words
                  {selectedExpressionEntry.user_state?.status && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                      {selectedExpressionEntry.user_state.status}
                    </span>
                  )}
                </div>
                {selectedExpressionEntry.entry.image_url && (
                  <img
                    src={selectedExpressionEntry.entry.image_url}
                    alt={selectedExpressionEntry.entry.word}
                    className="w-16 h-16 object-cover rounded-lg border border-purple-500/30 flex-shrink-0"
                    data-testid="expression-image"
                  />
                )}
              </div>
            </div>  {/* end header */}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {expressionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                </div>
              ) : (
                <>
                  {/* Translation */}
                  {selectedExpressionEntry.entry.translation_en && (
                    <div>
                      <h3 className="text-xs font-medium text-muted-foreground mb-1">Translation</h3>
                      <p className="text-sm">{selectedExpressionEntry.entry.translation_en}</p>
                    </div>
                  )}
                  
                  {/* Definitions */}
                  {selectedExpressionEntry.entry.definitions?.length > 0 ? (
                    <div>
                      <h3 className="text-xs font-medium text-muted-foreground mb-1">Definitions</h3>
                      {selectedExpressionEntry.entry.definitions.map((def, idx) => (
                        <div key={idx} className="text-sm mb-2">
                          {def.pos && <span className="text-purple-400 font-medium">[{def.pos}]</span>}
                          <span className="ml-1">{def.definition}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      No definitions yet
                    </div>
                  )}
                  
                  {/* Examples */}
                  {selectedExpressionEntry.entry.examples?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-muted-foreground mb-1">Examples</h3>
                      {selectedExpressionEntry.entry.examples.slice(0, 3).map((ex, idx) => (
                        <p key={idx} className="text-sm italic text-muted-foreground mb-1">"{ex}"</p>
                      ))}
                    </div>
                  )}
                  
                  {/* AI Enrich Button */}
                  {!selectedExpressionEntry.entry.definitions?.length && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      onClick={handleEnrichExpression}
                      disabled={expressionLoading}
                      data-testid="enrich-expression-btn"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Enrich Expression
                    </Button>
                  )}
                </>
              )}
            </div>
            
            {/* Status Footer */}
            <div className="p-4 border-t border-border">
              <div className="flex justify-center gap-2 flex-wrap" data-testid="expression-status-rail">
                <Button
                  size="sm"
                  variant={selectedExpressionEntry.user_state?.status === 'ignored' ? 'secondary' : 'outline'}
                  onClick={() => handleSetExpressionStatus('ignored')}
                  disabled={actionLoading}
                  className="text-xs"
                >
                  ✕
                </Button>
                <Button
                  size="sm"
                  variant={selectedExpressionEntry.user_state?.status === 'new' ? 'secondary' : 'outline'}
                  onClick={() => handleSetExpressionStatus('new')}
                  disabled={actionLoading}
                  className="text-xs"
                >
                  NEW
                </Button>
                <Button
                  size="sm"
                  variant={selectedExpressionEntry.user_state?.status === 'learning' ? 'secondary' : 'outline'}
                  onClick={() => handleSetExpressionStatus('learning')}
                  disabled={actionLoading}
                  className="text-xs text-blue-400"
                >
                  Review
                </Button>
                <Button
                  size="sm"
                  variant={selectedExpressionEntry.user_state?.status === 'familiar' ? 'secondary' : 'outline'}
                  onClick={() => handleSetExpressionStatus('familiar')}
                  disabled={actionLoading}
                  className="text-xs text-orange-400"
                >
                  Familiar
                </Button>
                <Button
                  size="sm"
                  variant={selectedExpressionEntry.user_state?.status === 'known' ? 'secondary' : 'outline'}
                  onClick={() => handleSetExpressionStatus('known')}
                  disabled={actionLoading}
                  className="text-xs text-green-400"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  Known
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Verb Formation mini card — shown on first click, before full oracle */}
      {selectedVerbFormationEntry && popoverPos && !verbFormationInFullOracle && createPortal(
        <div
          className="z-50 rounded-xl shadow-2xl border border-green-500/30 bg-background/95 backdrop-blur-sm"
          data-popover-panel
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            maxHeight: popoverPos.maxHeight,
            width: popoverPos.isMobile ? 'calc(100vw - 20px)' : '360px',
          }}
          data-testid="vf-mini-card"
        >
          <div className="flex flex-col overflow-hidden">
            {/* Drag handle */}
            <div
              onMouseDown={handleOracleDragStart}
              onTouchStart={handleOracleDragStart}
              className="flex items-center justify-center w-full select-none"
              style={{ height: 20, cursor: 'grab', flexShrink: 0 }}
              title="Drag to reposition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg>
            </div>
            {/* Header */}
            <div className="px-4 pb-3 border-b border-border/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <BookOpen className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-green-400 font-medium uppercase tracking-wider">Verb Formation</span>
                  {selectedVerbFormationEntry.entry.ai_enriched && (
                    <Sparkles className="h-3 w-3 text-green-400/50" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setVerbFormationInFullOracle(true)}
                    className="text-xs text-green-400 hover:text-green-300 border border-green-500/40 hover:border-green-400/60 rounded px-2 py-0.5 transition-colors"
                    data-testid="vf-open-full-oracle-btn"
                  >Full Oracle</button>
                  <button
                    onClick={closeVerbFormationMiniCard}
                    className="text-muted-foreground hover:text-foreground ml-1"
                    data-testid="vf-mini-close-btn"
                  >✕</button>
                </div>
              </div>
              <h2 className="text-xl font-bold text-green-400" data-testid="vf-mini-word">
                {selectedVerbFormationEntry.entry.word}
              </h2>
              {selectedVerbFormationEntry.entry.ipa && (
                <p className="text-xs text-muted-foreground mt-0.5">/{selectedVerbFormationEntry.entry.ipa}/</p>
              )}
            </div>
            {/* Quick content */}
            <div className="px-4 py-3 space-y-2 max-h-52 overflow-y-auto">
              {(selectedVerbFormationEntry.entry.translation_en || selectedVerbFormationEntry.entry.translation) && (
                <div className="rounded-lg bg-green-500/10 border border-green-500/20 px-3 py-2">
                  <p className="text-sm">{selectedVerbFormationEntry.entry.translation_en || selectedVerbFormationEntry.entry.translation}</p>
                </div>
              )}
              {Array.isArray(selectedVerbFormationEntry.entry.components) && selectedVerbFormationEntry.entry.components.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedVerbFormationEntry.entry.components.map((c, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-green-500/10 border border-green-500/20 text-green-400">{c}</span>
                  ))}
                  {selectedVerbFormationEntry.entry.formation_type && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted/40 border border-border/40 text-muted-foreground">{selectedVerbFormationEntry.entry.formation_type}</span>
                  )}
                </div>
              )}
              {!selectedVerbFormationEntry.entry.ai_enriched && (
                <p className="text-xs text-muted-foreground/50">AI enrichment pending…</p>
              )}
            </div>
            {/* Expand tab at bottom */}
            <button
              className="w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-green-500/20 bg-green-500/5 hover:bg-green-500/12 text-green-400 text-xs font-medium transition-colors rounded-b-xl"
              onClick={() => setVerbFormationInFullOracle(true)}
              data-testid="vf-expand-tab-btn"
            >
              <ChevronDown className="h-3.5 w-3.5" />
              Show full details
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Pagination */}
      <ReaderPagination page={currentPage} totalPages={totalPages} hasMore={hasMoreTokens} loadingMore={loadingMore} wordCount={pageTokens.filter(token => token.is_word).length} newWords={newWordsOnPage} sentenceMode={sentenceMode} sentenceIndex={currentSentenceIndex} sentenceCount={sentenceRanges.length} part={activePage?.part || 0} parts={activePage?.parts || 1} onPrevious={sentenceMode ? prevSentence : () => handlePageChange('prev')} onNext={sentenceMode ? nextSentence : () => handlePageChange('next')} onFinish={handleFinishText} finishLoading={finishLoading} ready={pagination.ready && !pagination.insufficientSpace} onPlaySentence={playSentenceAudio} playing={isPlayingSentence} playbackRate={readerPlaybackRate} onRateChange={setReaderPlaybackRate} onTranslateSentence={handleTranslateSentence} sentenceTranslation={sentenceTranslation} sentenceTranslating={sentenceTranslating} onAskSentence={() => setShowQAPanel(true)} />
      {/* Sentence QA Panel — rendered outside footer so it uses Sheet overlay */}
      {sentenceMode && (
        <SentenceQAPanel
          open={showQAPanel}
          onClose={() => setShowQAPanel(false)}
          textId={readerData?.text?.text_id}
          sentenceIndex={currentSentenceIndex}
          sentenceText={currentSentenceTokens.map(t => t.surface_form || '').join('').trim()}
          languageCode={readerData?.language?.code}
        />
      )}

      {/* Page Navigation Prompt - Mark New Words */}
      <AlertDialog open={showPagePrompt} onOpenChange={open => { if (!markingPage) setShowPagePrompt(open); }}>
        <AlertDialogContent data-testid="page-new-words-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" data-testid="page-new-words-title">
              <Flag className="h-5 w-5 text-red-500" />
              {newWordsOnPage} New Words on This Page
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="page-new-words-description">
              You have {newWordsOnPage} unmarked (red) words on this page. Would you like to mark them as known before continuing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pageMarkError && <p role="alert" className="text-sm text-destructive" data-testid="page-mark-error">{pageMarkError}</p>}
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              data-testid="skip-page-new-words"
              disabled={markingPage}
              onClick={() => {
                setShowPagePrompt(false);
                // Continue without marking
                if (pendingPageDirection === 'next') {
                  setCurrentPage(p => Math.min(p + 1, totalPages - 1));
                }
                setPendingPageDirection(null);
              }}
            >
              Skip & Continue
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="mark-page-new-words"
              disabled={markingPage}
              onClick={async event => {
                event.preventDefault(); // Keep the prompt open until saving succeeds.
                if (!(await markPageNewWordsAsKnown())) return;
                setShowPagePrompt(false);
                if (pendingPageDirection === 'next') {
                  setCurrentPage(p => Math.min(p + 1, totalPages - 1));
                }
                setPendingPageDirection(null);
              }}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {markingPage ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {markingPage ? 'Saving…' : 'Mark All as Known'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save to Language Dialog */}
      <Dialog open={showSaveLangDialog} onOpenChange={setShowSaveLangDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" /> Save "{selectedToken?.surface_form?.trim()}" to another language
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select language</label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent>
                  {availableLanguages.filter((l) => l.code !== language?.code).map((lang) => (
                    <SelectItem key={lang.language_id} value={lang.code}>{lang.name} ({lang.native_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="markKnown" checked={markAsKnown} onChange={(e) => setMarkAsKnown(e.target.checked)} className="rounded" />
              <label htmlFor="markKnown" className="text-sm">Mark as already known</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveLangDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveToLanguage} disabled={actionLoading || !selectedLanguage} data-testid="save-lang-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish Text Modal */}
      <Dialog open={showFinishModal} onOpenChange={open => { if (!finishActionBusy) setShowFinishModal(open); }}>
        <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" data-testid="lesson-complete-report" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl" data-testid="lesson-complete-title">
              <CheckCircle2 className="h-6 w-6 text-green-500" /> Lesson Complete!
            </DialogTitle>
          </DialogHeader>
          {finishStats && (
            <div className="space-y-6 py-4">
              {/* Title */}
              <div className="text-center">
                <p className="text-lg font-semibold">{finishStats.title}</p>
                <p className="text-sm text-muted-foreground">{finishStats.language}</p>
              </div>

              {/* Statistics */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Your Progress</h3>
                
                {/* Word counts by status */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-500">{finishStats.statistics?.by_status?.known || 0}</p>
                    <p className="text-xs text-muted-foreground">Known</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-500">{finishStats.statistics?.by_status?.familiar || 0}</p>
                    <p className="text-xs text-muted-foreground">Familiar</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-500">{finishStats.statistics?.by_status?.learning || 0}</p>
                    <p className="text-xs text-muted-foreground">Learning</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-500">{finishStats.statistics?.by_status?.ignored || 0}</p>
                    <p className="text-xs text-muted-foreground">Ignored</p>
                  </div>
                </div>

                {/* Comprehension */}
                <div className="bg-gradient-to-r from-amber-500/10 to-green-500/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Comprehension</span>
                    <span className="text-lg font-bold text-amber-500">{finishStats.statistics?.comprehension_percent}%</span>
                  </div>
                  <Progress value={finishStats.statistics?.comprehension_percent || 0} className="h-2 progress-gold" />
                </div>

                {/* Words marked known */}
                {finishStats.statistics?.words_marked_known > 0 && (
                  <p className="text-sm text-center text-muted-foreground">
                    <Sparkles className="h-4 w-4 inline mr-1 text-amber-500" />
                    {finishStats.statistics.words_marked_known} new words marked as known
                  </p>
                )}
                
                {/* Listen & Read counts */}
                <div className="flex justify-center gap-6 pt-2 border-t border-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Headphones className="h-4 w-4" />
                    <span>{finishStats.statistics?.listen_count || 0} listens</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    <span>{finishStats.statistics?.read_count || 0} reads</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <LessonCompleteActions key={textId} sourceTextId={textId} languageId={readerData?.language?.language_id} series={finishStats?.series} onBusyChange={setFinishActionBusy} onNavigate={path => { setShowFinishModal(false); navigate(path); }} />
        </DialogContent>
      </Dialog>

      {/* Multi-word Expression Selection Popup (near selection) */}
      {selectedExpression && selectedExpression.range && (() => {
        // Calculate position near the selection
        const rect = selectedExpression.range.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const popupWidth = Math.min(320, viewportWidth - 20);
        const popupHeight = 140;
        
        // Position below selection if space, otherwise above
        let top = rect.bottom + 8;
        if (top + popupHeight > viewportHeight - 20) {
          top = rect.top - popupHeight - 8;
        }
        
        // Center horizontally on selection
        let left = rect.left + (rect.width / 2) - (popupWidth / 2);
        left = Math.max(10, Math.min(left, viewportWidth - popupWidth - 10));
        
        return createPortal(
          <div 
            className="fixed bg-card border border-border rounded-xl shadow-xl z-50 p-4"
            style={{ 
              top: `${Math.max(10, top)}px`, 
              left: `${left}px`,
              width: `${popupWidth}px`,
            }}
            data-testid="expression-popup"
            data-popover-panel
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-sm">"{selectedExpression.text}"</span>
                {selectedExpression.tokens?.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedExpression.tokens.length} words
                  </Badge>
                )}
              </div>
              <Button 
                size="icon" 
                variant="ghost"
                className="h-6 w-6"
                onClick={() => {
                  setSelectedExpression(null);
                  window.getSelection()?.removeAllRanges();
                }}
                data-testid="cancel-expression-btn"
              >
                <span className="text-muted-foreground">✕</span>
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                onClick={handleSaveExpression}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="save-expression-btn"
              >
                <Save className="h-3 w-3 mr-1" />
                Save
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  await handleSaveExpression();
                  // After saving, try to enrich
                  toast.info('Expression saved! Enriching with AI...');
                }}
                data-testid="save-enrich-expression-btn"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Save + AI
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => {
                  // Quick mark as known
                  handleSaveExpression().then(() => {
                    toast.success('Expression saved and marked as known');
                  });
                }}
                className="text-green-600 hover:text-green-700"
                data-testid="save-known-expression-btn"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Save Known
              </Button>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Flashcard List Modal */}
      <Dialog open={showFlashcardModal} onOpenChange={setShowFlashcardModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5" />
              Add to Flashcard List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Save <strong dir="auto">{selectedToken?.surface_form || selectedToken?.normalized_form}</strong> to a flashcard list:
            </p>
            {flashcardLists.length > 0 && (
              <Select value={selectedFlashcardList} onValueChange={setSelectedFlashcardList}>
                <SelectTrigger><SelectValue placeholder="Select existing list..." /></SelectTrigger>
                <SelectContent>
                  {flashcardLists.map(list => (
                    <SelectItem key={list.list_id} value={list.list_id}>
                      {list.name} ({list.word_count || 0} words)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Or create:</span>
              <Input
                placeholder="New list name..."
                value={newFlashcardListName}
                onChange={(e) => { setNewFlashcardListName(e.target.value); setSelectedFlashcardList(''); }}
                className="text-sm"
              />
            </div>
            <Button onClick={handleConfirmFlashcard} disabled={savingFlashcard || (!selectedFlashcardList && !newFlashcardListName.trim())} className="w-full">
              {savingFlashcard ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Flashcard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



